import { computed, ref, watch, type ComputedRef, type Ref, type WritableComputedRef } from 'vue';
import type { MessageApi } from 'naive-ui';
import {
  type ConvertEntitiesState,
  SketchEntitiesTransformCommand,
  SketchEntitiesTransformInputMode,
  SketchEntitiesTransformRelationRetentionMode,
  SketchEntitiesTransformSelectionRole,
  type SketchEntitiesTransformState,
  isSketchCornerMode,
  SketchCornerMode,
  SketchTrimMode,
  SketchTrimSingleEntityBehavior,
  type SketchTrimEntityRef,
  type SketchTrimMode as SketchTrimModeType,
  type SketchTrimOptions as SketchTrimOptionsType,
} from '@/core';
import { isDefaultDatumPlaneSummary, usePlaneStore } from '@/hooks/usePlane';
import { useDrawingStore } from '@/hooks/useDrawing';
import { useFeatureTreeStore, FeatureTreeNodeKind } from '@/hooks/useFeatureTree';
import { hasFeatureTreeDatumPlaneId } from '@/hooks/feature-tree/feature-tree-types';
import { useSketchPropertyVisible } from '@/hooks/useSketchPropertyVisible';
import {
  isIdleViewerMode,
  useSketchStateStore,
} from '@/hooks/useSketchState';
import { useToolbarCommandStore } from '@/stores/toolbar-command';
import type { ViewportSelectionSnapshot } from '@/composables/extrude-types';
import { storeToRefs } from 'pinia';
import {
  EditorPanelShowDesignTreeReason,
  canExecuteImportedDocumentToolbarCommand,
  SketchArcCommand,
    SketchCircleCommand,
    SketchDrawingTool,
  SketchLineCommand,
  type SketchPlanarFaceSelection,
  SketchRectangleCommand,
  SketchSlotCommand,
  SketchSplineCommand,
  StyleSplineMode,
  ViewerMode,
  ViewMode,
  ReferenceCreationContext,
  type Viewer,
  type PointSelectionCandidate,
  isSketchArcCommand,
  isSketchCircleCommand,
  isSketchDrawingCommand as isSketchDrawingCommandByManager,
  isSketchEllipseCommand,
  isSketchLineCommand,
  isSketchPointCommand,
  isSketchPolygonCommand,
  isSketchRectangleCommand,
  isSketchSlotCommand,
  isSketchSplineCommand,
  isStaleWorkerResponseError,
  type CoverType,
} from '@puppycad/cad-core';
import { requestEditorDesignTreeTab } from '@/views/editor/requestEditorDesignTreeTab';
import type { EditorViewportEmptySelectionSource } from '@/views/editor/registerEditorSelectionSubscriptions';

/**
 * 草图修剪命令。
 */
enum SketchTrimCommand {
  /** 修剪实体。 */
  TrimEntities = 'sketch-trim-entities',
  /** 延伸实体。 */
  ExtendEntities = 'sketch-extend-entities',
}

/**
 * 草图修剪命令配置。
 */
interface SketchTrimCommandConfig {
  /** PropertyManager 对应模式。 */
  mode: SketchTrimModeType;
  /** 单实体模式行为。 */
  singleEntityBehavior: SketchTrimSingleEntityBehavior;
  /** 工具栏显示名称。 */
  name: string;
  /** 工具栏显示提示。 */
  hint: string;
}

/**
 * 修剪工作流清理选项。
 */
interface ClearSketchTrimWorkflowInput {
  /** 是否同步清理工具栏命令状态。 */
  clearToolbarState?: boolean;
  /** 是否同步清空当前主选择。 */
  clearSelection?: boolean;
}

/**
 * 退出草图模式输入。
 */
interface ExitSketchModeInput {
  /** 是否静默退出，避免命令切换时额外提示“已退出草图”。 */
  silent?: boolean;
  /**
   * 是否允许命令切换主动停止当前绘制工具。
   *
   * 说明：手动退出草图时仍要求用户显式完成或取消绘制；只有拉伸、旋转等非草图命令
   * 主动接管流程时，才允许清理未提交的绘制预览并继续退出草图。
   */
  allowDrawingStop?: boolean;
}

//------ 草图圆角命令收口 开始------
/**
 * 草图圆角/倒角命令配置。
 */
interface SketchCornerCommandConfig {
  /** 工具栏与 PropertyManager 显示名称。 */
  name: string;
  /** 工具栏显示提示。 */
  hint: string;
}
//------ 草图圆角命令收口 结束------

/**
 * 判断键盘事件是否来自可编辑输入控件。
 *
 * @param target 事件目标。
 * @returns 来自输入控件时返回 true。
 */
function isEditableKeyboardTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  if (target.isContentEditable) {
    return true;
  }

  // 输入组件内部可能存在包裹元素，必须沿父级查找真实可编辑控件，避免全局草图快捷键抢占文本输入。
  return target.closest('input, textarea, select, [contenteditable="true"], [contenteditable="plaintext-only"]') !== null;
}

//------ 命令结束显示设计树 开始------
/**
 * 请求命令结束后右侧面板回到设计树。
 *
 * @param reason 命令收口原因。
 * @param command 当前收口的命令标识。
 */
function requestDesignTreeAfterSketchCommand(
  reason: EditorPanelShowDesignTreeReason,
  command: string | null,
): void {
  // 草图工作流只发布切换意图，实际 tab 状态由 Editor.vue 的事件监听器统一维护。
  requestEditorDesignTreeTab(reason, command);
}
//------ 命令结束显示设计树 结束------

/**
 * 解析草图实体变换命令对应的 Viewer 模式。
 *
 * @param commandType 草图实体变换命令。
 * @returns 对应 Viewer 模式。
 */
function resolveSketchEntitiesTransformViewerMode(commandType: SketchEntitiesTransformCommand): ViewerMode {
  switch (commandType) {
    case SketchEntitiesTransformCommand.Move:
      return ViewerMode.SketchMoveEntities;
    case SketchEntitiesTransformCommand.Copy:
      return ViewerMode.SketchCopyEntities;
    case SketchEntitiesTransformCommand.Rotate:
      return ViewerMode.SketchRotateEntities;
    case SketchEntitiesTransformCommand.Mirror:
      return ViewerMode.SketchMirrorEntities;
    default:
      return ViewerMode.Sketch;
  }
}

/**
 * 草图工作流组合函数参数。
 */
/**
 * 草图会话历史输入。
 *
 * 设计说明：
 * workflow 只声明会话边界，不直接持有历史快照或 Three.js 对象；
 * 具体 before/after 捕获由 Editor 通过回调注入，保持 workflow 与历史实现解耦。
 */
export interface SketchSessionHistoryInput {
  /** 草图组 ID。 */
  readonly sketchGroupId: string;
  /** 用户可读历史标签。 */
  readonly label: string;
  /** 历史命令类型。 */
  readonly commandType: string;
}

/**
 * 草图退出历史结果。
 */
export interface SketchSessionExitHistoryResult {
  /** 退出草图时是否因空草图被删除。 */
  readonly removed: boolean;
}

/**
 * 草图退出历史草稿。
 */
export type SketchSessionExitHistoryDraft = unknown;

export interface UseEditorSketchWorkflowOptions {
  /** 读取当前 Viewer 运行时句柄。 */
  getViewer: () => Viewer | null;
  /** 消息实例。 */
  message: MessageApi;
  /** 右侧面板标签。 */
  rightPanelTab: Ref<string>;
  /** 当前文档是否允许执行特征命令。 */
  supportsFeatureCommands: ComputedRef<boolean> | Ref<boolean>;
  /** 是否处于拉伸模式。 */
  isExtrudeMode: ComputedRef<boolean> | Ref<boolean>;
  /** 拉伸 UI 状态。 */
  extrudeUiState: ComputedRef<{ operation: 'boss' | 'cut' }> | Ref<{ operation: 'boss' | 'cut' }>;
  /** 拉伸命令入口。 */
  handleExtrudeCommand: (preselectedContourObjectIds?: readonly string[]) => Promise<void>;
  /** 拉伸切除命令入口。 */
  handleCutExtrudeCommand: (preselectedContourObjectIds?: readonly string[]) => Promise<void>;
  /** 拉伸选择变化处理。 */
  handleExtrudeSelectionChange: (selection: ViewportSelectionSnapshot | string | null) => Promise<boolean>;
  /** 取消拉伸。 */
  cancelExtrude: () => boolean;
  /** 旋转 UI 状态。 */
  revolveUiState: ComputedRef<{ active: boolean }> | Ref<{ active: boolean }>;
  /** 旋转命令入口。 */
  handleRevolveCommand: () => Promise<void>;
  /** 旋转选择变化处理。 */
  handleRevolveSelectionChange: (selection: ViewportSelectionSnapshot | string | null) => Promise<boolean>;
  /** 取消旋转。 */
  cancelRevolve: () => boolean;
  /** 旋转切除 UI 状态。 */
  revolveCutUiState: ComputedRef<{ active: boolean }> | Ref<{ active: boolean }>;
  /** 旋转切除命令入口。 */
  handleRevolveCutCommand: () => Promise<void>;
  /** 旋转切除选择变化处理。 */
  handleRevolveCutSelectionChange: (selection: ViewportSelectionSnapshot | string | null) => Promise<boolean>;
  /** 取消旋转切除。 */
  cancelRevolveCut: () => boolean;
  /** 当前是否处于圆角/倒角模式。 */
  isBlendMode: ComputedRef<boolean> | Ref<boolean>;
  /** 当前活跃 blend 类型。 */
  activeBlendKind: ComputedRef<'fillet' | 'chamfer' | null> | Ref<'fillet' | 'chamfer' | null>;
  /** 圆角命令入口。 */
  handleFilletCommand: () => void;
  /** 倒角命令入口。 */
  handleChamferCommand: () => void;
  /** 圆角/倒角选择变化处理。 */
  handleBlendSelectionChange: (selection?: ViewportSelectionSnapshot) => Promise<boolean>;
  /** 取消圆角/倒角。 */
  cancelBlend: () => void;
  /** 多实体框选标记。 */
  multipleEntitiesBoxSelected: Ref<boolean>;
  /** 刷新状态回调。 */
  updateState: () => Promise<void>;
  /** 提交新建草图会话历史。 */
  commitSketchSessionCreateHistory?: (input: SketchSessionHistoryInput) => void | Promise<void>;
  /** 捕获退出草图会话前的历史草稿。 */
  captureSketchSessionExitHistory?: (input: SketchSessionHistoryInput) => SketchSessionExitHistoryDraft | null;
  /** 提交退出草图会话历史。 */
  commitSketchSessionExitHistory?: (
    draft: SketchSessionExitHistoryDraft,
    result: SketchSessionExitHistoryResult,
  ) => void | Promise<void>;
  /** 释放未提交的退出草图会话历史草稿。 */
  releaseSketchSessionExitHistory?: (draft: SketchSessionExitHistoryDraft) => void;
  /** 草图状态仓库。 */
  sketchStateStore: ReturnType<typeof useSketchStateStore>;
  /** 绘图状态仓库。 */
  drawingStore: ReturnType<typeof useDrawingStore>;
  /** 基准面状态仓库。 */
  planeStore: ReturnType<typeof usePlaneStore>;
  /** 特征树仓库。 */
  featureTreeStore: ReturnType<typeof useFeatureTreeStore>;
  /** 上色草图轮廓开关。 */
  shadedSketchContoursEnabledRef: Ref<boolean> | WritableComputedRef<boolean>;
  /** 开始插入零部件。 */
  startInsertComponent?: () => void;
  /** 取消插入零部件。 */
  cancelInsertComponent?: () => void;
  /** 是否处于零部件放置模式。 */
  isPlacingComponent?: Ref<boolean>;
  /** 取消零部件放置。 */
  cancelComponentPlacement?: () => void;
  /** 结束零部件放置流程（含状态重置）。 */
  finishPlacement?: () => void;
  /** Convert Entities 命令会话状态。 */
  convertEntitiesState?: Ref<ConvertEntitiesState>;
  /** 进入 Convert Entities 命令会话。 */
  enterConvertEntities?: () => void;
  /** 确认 Convert Entities 命令会话。 */
  confirmConvertEntities?: () => boolean | Promise<boolean>;
  /** 取消 Convert Entities 命令会话。 */
  cancelConvertEntities?: () => void;
}

/**
 * 草图工作流宿主组合函数。
 *
 * 设计说明：
 * 1. 将 Editor 中与草图命令、修剪、倒角直接相关的超长逻辑整体抽离；
 * 2. 这里只处理宿主会话状态与 Viewer 公共接口的协同，不下沉业务真相；
 * 3. 返回值全部为显式状态和命令，便于 Editor 继续做薄编排。
 */
export function useEditorSketchWorkflow(options: UseEditorSketchWorkflowOptions) {
  const {
    getViewer,
    message,
    rightPanelTab,
    isExtrudeMode,
    extrudeUiState,
    handleExtrudeCommand,
    handleCutExtrudeCommand,
    handleExtrudeSelectionChange,
    cancelExtrude,
    revolveUiState,
    handleRevolveCommand,
    handleRevolveSelectionChange,
    cancelRevolve,
    revolveCutUiState,
    handleRevolveCutCommand,
    handleRevolveCutSelectionChange,
    cancelRevolveCut,
    isBlendMode,
    activeBlendKind,
    handleFilletCommand,
    handleChamferCommand,
    handleBlendSelectionChange,
    cancelBlend,
    multipleEntitiesBoxSelected,
    updateState,
    commitSketchSessionCreateHistory,
    captureSketchSessionExitHistory,
    commitSketchSessionExitHistory,
    releaseSketchSessionExitHistory,
    sketchStateStore,
    drawingStore,
    planeStore,
    featureTreeStore,
    shadedSketchContoursEnabledRef,
    convertEntitiesState: externalConvertEntitiesState,
    enterConvertEntities,
    confirmConvertEntities: confirmExternalConvertEntities,
    cancelConvertEntities,
  } = options;

  const {
    viewerMode,
    isSketchActive,
    currentSketchGroupId,
    pendingSketchToolCommand,
  } = storeToRefs(sketchStateStore);
  const {
    isDrawingMode,
    currentDrawingTool,
    lineDrawingPointCount,
    drawingPointCount,
  } = storeToRefs(drawingStore);
  const {
    currentDrawingDatumPlaneId,
    selectedDatumPlaneId,
  } = storeToRefs(planeStore);
  const {
    selectedKeys,
    selectedObject,
  } = storeToRefs(featureTreeStore);
  const toolbarCommandStore = useToolbarCommandStore();

  const sketchTrimCommandConfig: Record<SketchTrimCommand, SketchTrimCommandConfig> = {
    [SketchTrimCommand.TrimEntities]: {
      mode: SketchTrimMode.Power,
      singleEntityBehavior: SketchTrimSingleEntityBehavior.Trim,
      name: '修剪实体',
      hint: '强力修剪：按住左键拖动连续修剪，Shift+拖动可延伸，回拖红方块可撤销一步',
    },
    [SketchTrimCommand.ExtendEntities]: {
      mode: SketchTrimMode.TrimToClosest,
      singleEntityBehavior: SketchTrimSingleEntityBehavior.Extend,
      name: '延伸实体',
      hint: '延伸实体：在强力修剪中按 Shift 拖动延伸',
    },
  };

  const defaultSketchTrimOptions: SketchTrimOptionsType = {
    mode: SketchTrimMode.Power,
    singleEntityBehavior: SketchTrimSingleEntityBehavior.Trim,
    keepTrimmedAsConstruction: false,
    ignoreConstructionGeometry: false,
  };

  //------ 草图圆角命令收口 开始------
  /**
   * 草图圆角/倒角命令元数据。
   */
  const sketchCornerCommandConfig: Record<SketchCornerMode, SketchCornerCommandConfig> = {
    [SketchCornerMode.Fillet]: {
      name: '草图圆角',
      hint: '草图圆角：选择两个受支持实体，或选择它们的共享角点',
    },
    [SketchCornerMode.Chamfer]: {
      name: '草图倒角',
      hint: '草图倒角：选择两条相邻直线，或选择它们的共享角点',
    },
  };
  //------ 草图圆角命令收口 结束------

  const shadedSketchContoursEnabled = shadedSketchContoursEnabledRef;
  const activeOperationCommand = ref<string | null>(null);
  const currentDrawingCommand = ref<string | null>(null);
  const toolbarOperationOverride = ref<{ name: string; hint: string } | null>(null);
  const sketchCornerEnable = ref(false);
  const sketchCornerMode = ref<SketchCornerMode>(SketchCornerMode.Chamfer);
  const sketchTrimMode = ref<SketchTrimModeType | null>(null);
  const sketchTrimOptions = ref<SketchTrimOptionsType>({ ...defaultSketchTrimOptions });
  //------ 智能尺寸命令 开始------
  /** 智能尺寸命令是否运行中（真状态源是 Viewer 控制器快照，这里只承载工具栏/Esc 路由）。 */
  const smartDimensionActive = ref(false);
  //------ 智能尺寸命令 结束------
  const selectedSketchTrimEntity = ref<SketchTrimEntityRef | { objectId: string; segmentIndex: number | null; hitPoint?: [number, number, number] } | null>(null);
  //------ 等距实体状态 开始------
  const sketchOffsetDistance = ref<number>(10);
  const sketchOffsetReverse = ref<boolean>(false);
  const sketchOffsetBidirectional = ref<boolean>(false);
  const sketchOffsetAddSize = ref<boolean>(false);
  const sketchOffsetSelectChain = ref<boolean>(true);
  const sketchOffsetCover = ref<{ hasCover: boolean; type: CoverType }>({ hasCover: false, type: 'circularArc' });
  const sketchOffsetBasicGeometry = ref<boolean>(false);
  const sketchOffsetOffsetGeometry = ref<boolean>(false);
  //------ 等距实体状态 结束------
  const activeIdleViewerMode = ref<ViewerMode>(ViewerMode.Default);
  const convertEntitiesState = externalConvertEntitiesState ?? ref<ConvertEntitiesState>({
    active: false,
    targetSketchGroupId: null,
    selectedItems: [],
    asConstruction: false,
    selectChain: false,
    canConfirm: false,
  });

  //------ 草图实体变换 UI 工作流 开始------
  /**
   * 创建草图实体变换默认状态。
   *
   * @param command 当前命令；未激活时为 null。
   * @returns 草图实体变换状态。
   */
  const createDefaultSketchEntitiesTransformState = (
    command: SketchEntitiesTransformCommand | null,
  ): SketchEntitiesTransformState => ({
    active: command !== null,
    command,
    inputMode: SketchEntitiesTransformInputMode.FromTo,
    activeSelectionRole: SketchEntitiesTransformSelectionRole.Entities,
    relationRetentionMode: SketchEntitiesTransformRelationRetentionMode.BreakExternalRelations,
    selectedItems: [],
    fromPoint: null,
    toPoint: null,
    rotationCenter: null,
    targetX: command === SketchEntitiesTransformCommand.Move ? 0 : null,
    targetY: command === SketchEntitiesTransformCommand.Move ? 0 : null,
    angleDeg: command === SketchEntitiesTransformCommand.Rotate ? 0 : null,
    canConfirm: false,
    canRepeat: false,
  });

  /** 草图实体变换命令会话状态。 */
  const sketchEntitiesTransformState = ref<SketchEntitiesTransformState>(
    createDefaultSketchEntitiesTransformState(null),
  );
  //------ 草图实体变换 UI 工作流 结束------

  const hasPendingSketchCommand = computed(() => Boolean(pendingSketchToolCommand.value));
  const idleViewerMode = computed(() => activeIdleViewerMode.value);
  const isIdleViewerModeActive = computed(() => viewerMode.value === idleViewerMode.value);
  const isSketchEditMode = computed(() => (
    viewerMode.value === ViewerMode.Sketch
    || viewerMode.value === ViewerMode.SketchDrawing
    || viewerMode.value === ViewerMode.SketchTrim
    //------ 草图实体变换退出草图收口 开始------
    /**
     * 草图实体变换命令仍然运行在同一个草图编辑会话内。
     * 若这里不把 Move / Copy / Rotate 视为草图编辑态，退出草图会在
     * `exitSketchMode` 中提前返回，导致 cad-core 的活动草图会话无法结束。
     */
    || viewerMode.value === ViewerMode.SketchMoveEntities
    || viewerMode.value === ViewerMode.SketchCopyEntities
    || viewerMode.value === ViewerMode.SketchRotateEntities
    || viewerMode.value === ViewerMode.SketchCircularPattern
    || viewerMode.value === ViewerMode.SketchMirrorEntities
    || viewerMode.value === ViewerMode.SketchOffset
    //------ 草图实体变换退出草图收口 结束------
  ));
  const selectedObjectName = computed(() => featureTreeStore.selectedObjectName);

  const {
    linePropertyVisible,
    arcPropertyVisible,
    circlePropertyVisible,
    ellipsePropertyVisible,
    polygonPropertyVisible,
    rectanglePropertyVisible,
    splinePropertyVisible,
    styleSplinePropertyVisible,
    slotPropertyVisible,
    pointPropertyVisible,
    insertLinePropertyVisible,
  } = useSketchPropertyVisible({
    sketchCornerEnable,
    activeCommand: activeOperationCommand,
    currentDrawingCommand,
    multipleEntitiesBoxSelected,
  });

  /**
   * 要求当前存在 Viewer。
   *
   * @returns 当前 Viewer。
   */
  const requireViewer = (): Viewer => {
    const viewer = getViewer();
    if (!viewer) {
      throw new Error('Viewer 未初始化，无法执行草图工作流。');
    }
    return viewer;
  };

  watch(
    viewerMode,
    (mode) => {
      if (isIdleViewerMode(mode)) {
        activeIdleViewerMode.value = mode;
      }
    },
    { immediate: true },
  );

  /**
   * 解析基准点选择候选。
   *
   * @param meshId 场景对象 ID。
   * @returns 基准点候选；不支持时返回 null。
   */
  const resolvePointSelectionCandidate = (meshId: string): PointSelectionCandidate | null => {
    const viewer = getViewer() as unknown as {
      referencePointController?: {
        resolveSelectionCandidateByObjectId: (objectId: string) => PointSelectionCandidate | null;
      };
    } | null;
    return viewer?.referencePointController?.resolveSelectionCandidateByObjectId(meshId) ?? null;
  };

  /**
   * 参考几何体命令类型。
   */
  type ReferenceFeatureCommandType =
    | 'geometry-reference-plane'
    | 'geometry-reference-axis'
    | 'geometry-reference-point'
    | 'assembly-reference-plane'
    | 'assembly-reference-axis'
    | 'assembly-reference-point';

  /**
   * 参考几何体命令分类。
   */
  type ReferenceFeatureCommandKind = 'plane' | 'axis' | 'point';

  /**
   * 参考几何体命令元数据。
   */
  interface ReferenceFeatureCommandMeta {
    /**
     * 命令几何类别。
     */
    kind: ReferenceFeatureCommandKind;
    /**
     * 工具栏与面板显示名称。
     */
    name: string;
    /**
     * 工具栏提示文案。
     */
    hint: string;
  }

  /**
   * 参考几何体命令元数据表。
   */
  const REFERENCE_FEATURE_COMMAND_META: Record<ReferenceFeatureCommandType, ReferenceFeatureCommandMeta> = {
    'geometry-reference-plane': {
      kind: 'plane',
      name: '基准面',
      hint: '按顺序选择参考实体，确认后创建基准面。',
    },
    'geometry-reference-axis': {
      kind: 'axis',
      name: '基准轴',
      hint: '按顺序选择参考实体，确认后创建基准轴。',
    },
    'geometry-reference-point': {
      kind: 'point',
      name: '基准点',
      hint: '按顺序选择参考实体，确认后创建基准点。',
    },
    'assembly-reference-plane': {
      kind: 'plane',
      name: '装配体基准面',
      hint: '按顺序选择装配体参考实体，确认后创建装配体基准面。',
    },
    'assembly-reference-axis': {
      kind: 'axis',
      name: '装配体基准轴',
      hint: '按顺序选择装配体参考实体，确认后创建装配体基准轴。',
    },
    'assembly-reference-point': {
      kind: 'point',
      name: '装配体基准点',
      hint: '按顺序选择装配体参考实体，确认后创建装配体基准点。',
    },
  };

  /**
   * 参考几何体命令集合。
   */
  const REFERENCE_FEATURE_COMMAND_SET = new Set<ReferenceFeatureCommandType>(
    Object.keys(REFERENCE_FEATURE_COMMAND_META) as ReferenceFeatureCommandType[],
  );

  /**
   * 判断命令是否属于参考几何体命令。
   *
   * @param commandType 命令标识。
   * @returns 是否为参考几何体命令。
   */
  const isReferenceFeatureCommand = (commandType: string | null): commandType is ReferenceFeatureCommandType => {
    if (!commandType) {
      return false;
    }
    return REFERENCE_FEATURE_COMMAND_SET.has(commandType as ReferenceFeatureCommandType);
  };

  /**
   * 读取参考几何体命令元数据。
   *
   * @param commandType 参考几何体命令。
   * @returns 对应元数据。
   */
  const resolveReferenceFeatureCommandMeta = (commandType: ReferenceFeatureCommandType): ReferenceFeatureCommandMeta => {
    return REFERENCE_FEATURE_COMMAND_META[commandType];
  };

  /**
   * 判断命令是否属于基准面类命令。
   *
   * @param commandType 命令标识。
   * @returns 是否属于基准面类命令。
   */
  const isReferencePlaneCommand = (commandType: string | null): commandType is ReferenceFeatureCommandType => {
    return isReferenceFeatureCommand(commandType)
      && resolveReferenceFeatureCommandMeta(commandType).kind === 'plane';
  };

  /**
   * 判断命令是否属于基准轴类命令。
   *
   * @param commandType 命令标识。
   * @returns 是否属于基准轴类命令。
   */
  const isReferenceAxisCommand = (commandType: string | null): commandType is ReferenceFeatureCommandType => {
    return isReferenceFeatureCommand(commandType)
      && resolveReferenceFeatureCommandMeta(commandType).kind === 'axis';
  };

  /**
   * 判断命令是否属于基准点类命令。
   *
   * @param commandType 命令标识。
   * @returns 是否属于基准点类命令。
   */
  const isReferencePointCommand = (commandType: string | null): commandType is ReferenceFeatureCommandType => {
    return isReferenceFeatureCommand(commandType)
      && resolveReferenceFeatureCommandMeta(commandType).kind === 'point';
  };

  /**
   * 判断命令是否属于修剪族。
   *
   * @param commandType 命令标识。
   * @returns 是否属于修剪族。
   */
  const isSketchTrimCommand = (commandType: string): commandType is SketchTrimCommand => {
    return commandType === SketchTrimCommand.TrimEntities
      || commandType === SketchTrimCommand.ExtendEntities;
  };

  /**
   * 会在进入 PropertyManager 时吸收当前草图轮廓选择的特征命令集合。
   *
   * 说明：这些命令需要复用退出草图时自动选中的轮廓，不能被通用命令切换清理提前清空。
   */
  const PROFILE_FEATURE_SELECTION_CONSUMING_COMMANDS = new Set<string>([
    'extrude',
    'extrude-cut',
    'revolve-boss',
    'revolve-cut',
  ]);

  /**
   * 判断命令启动时是否应该保留当前草图轮廓选择。
   *
   * @param commandType 顶层命令标识。
   * @returns 命令会吸收当前草图轮廓选择时返回 true。
   */
  const shouldPreserveSelectionForProfileFeatureCommand = (commandType: string): boolean => {
    return PROFILE_FEATURE_SELECTION_CONSUMING_COMMANDS.has(commandType);
  };

  /**
   * 判断命令启动时是否需要保留当前视口预选。
   *
   * 设计说明：草图入口需要先把当前平面面解析为草图宿主，不能被通用命令清理提前清空；
   * 等距实体与 SolidWorks 一致需要吸收命令激活前已选中的草图实体；
   * 轮廓特征则继续复用原有预选吸收规则，三者共享同一命令生命周期边界。
   *
   * @param commandType 顶层命令标识。
   * @returns 当前命令会消费视口预选时返回 true。
   */
  const shouldPreserveSelectionForCommandStart = (commandType: string): boolean => {
    return commandType === 'sketch-enter'
      || commandType === 'sketch-offset-entities'
      || shouldPreserveSelectionForProfileFeatureCommand(commandType);
  };

  /**
   * 读取轮廓特征命令启动边界的预选对象快照。
   *
   * 设计说明：
   * 1. 自动退出草图会在 cad-core 内重新选中本次草图形状，特征命令必须消费这一刻的稳定结果；
   * 2. 命令初始化阶段可能切换模式或清理预览，不能把“稍后再回读 viewer 当前选择”作为唯一数据源；
   * 3. 返回数组副本，避免后续选择集合突变影响 PropertyManager 默认吸收。
   *
   * @returns 当前轮廓特征预选对象 ID 快照。
   */
  const readProfileFeaturePreselectionSnapshot = (): string[] => {
    return [...(getViewer()?.getSelectedObjectIds?.() ?? [])];
  };

  /**
   * 解析修剪命令配置。
   *
   * @param commandType 修剪命令。
   * @returns 对应配置。
   */
  const resolveSketchTrimCommandConfig = (commandType: SketchTrimCommand): SketchTrimCommandConfig => {
    return sketchTrimCommandConfig[commandType];
  };

  /**
   * 判断当前是否处于修剪工作流。
   *
   * @returns 是否处于修剪工作流。
   */
  const isSketchTrimWorkflowActive = (): boolean => {
    const activeCommand = activeOperationCommand.value;
    return sketchTrimMode.value !== null
      || (activeCommand !== null && isSketchTrimCommand(activeCommand));
  };

  /**
   * 判断当前是否处于 Convert Entities 工作流。
   *
   * @returns 是否处于 Convert Entities 工作流。
   */
  const isConvertEntitiesWorkflowActive = (): boolean => {
    return convertEntitiesState.value.active === true
      || activeOperationCommand.value === 'sketch-convert-entities';
  };

  //------ 草图实体变换 UI 工作流 开始------
  /**
   * 判断命令是否属于草图实体变换命令。
   *
   * @param commandType 命令标识。
   * @returns 是否为草图实体变换命令。
   */
  const isSketchEntitiesTransformCommand = (
    commandType: string | null,
  ): commandType is SketchEntitiesTransformCommand => {
    return commandType === SketchEntitiesTransformCommand.Move
      || commandType === SketchEntitiesTransformCommand.Copy
      || commandType === SketchEntitiesTransformCommand.Rotate
      || commandType === SketchEntitiesTransformCommand.Mirror;
  };

  /**
   * 读取草图实体变换命令显示名称。
   *
   * @param commandType 命令标识。
   * @returns 命令显示名称。
   */
  const resolveSketchEntitiesTransformName = (commandType: SketchEntitiesTransformCommand): string => {
    if (commandType === SketchEntitiesTransformCommand.Copy) {
      return '复制实体';
    }
    if (commandType === SketchEntitiesTransformCommand.Rotate) {
      return '旋转实体';
    }
    if (commandType === SketchEntitiesTransformCommand.Mirror) {
      return '镜向实体';
    }
    return '移动实体';
  };

  /**
   * 判断当前是否处于草图实体变换 UI 工作流。
   *
   * @returns 是否处于草图实体变换 UI 工作流。
   */
  const isSketchEntitiesTransformWorkflowActive = (): boolean => {
    return sketchEntitiesTransformState.value.active === true
      || isSketchEntitiesTransformCommand(activeOperationCommand.value);
  };
  //------ 草图实体变换 UI 工作流 结束------

  /**
   * 同步工具栏操作状态。
   *
   * @param command 当前命令。
   * @param operation 工具栏提示。
   */
  const syncToolbarOperationState = (
    command: string | null,
    operation: { name: string; hint: string } | null,
  ): void => {
    activeOperationCommand.value = command;
    toolbarOperationOverride.value = operation;
    if (command) {
      toolbarCommandStore.setActiveCommands([command]);
      return;
    }
    if (currentDrawingCommand.value) {
      toolbarCommandStore.setActiveCommands([currentDrawingCommand.value]);
      return;
    }
    toolbarCommandStore.clearActiveCommands();
  };

  /**
   * 激活草图编辑入口的工具栏状态。
   *
   * 设计说明：
   * 1. 从“草图绘制”按钮进入草图时会保留 `sketch-enter` 高亮；
   * 2. 从设计树恢复已有草图编辑也属于同一类草图编辑会话，不能清成空命令；
   * 3. 将该状态集中在工作流内部写入，避免设计树入口自行补 UI 状态。
   */
  const activateSketchEditingToolbarState = (): void => {
    syncToolbarOperationState('sketch-enter', {
      name: '草图绘制',
      hint: '正在编辑草图，可修改草图实体或选择绘制工具。',
    });
  };

  watch([isExtrudeMode, () => extrudeUiState.value.operation], ([active]) => {
    if (active) {
      const isCutOperation = extrudeUiState.value.operation === 'cut';
      rightPanelTab.value = 'property';
      syncToolbarOperationState(isCutOperation ? 'extrude-cut' : 'extrude', {
        name: isCutOperation ? '拉伸切除' : '拉伸凸台/基体',
        hint: isCutOperation
          ? '请选择同一草图中的闭合轮廓以执行切除。'
          : '请选择同一草图中的闭合轮廓。',
      });
      return;
    }
    if (activeOperationCommand.value === 'extrude' || activeOperationCommand.value === 'extrude-cut') {
      syncToolbarOperationState(null, null);
    }
  });

  watch([isBlendMode, activeBlendKind], ([active, kind]) => {
    if (active && kind) {
      rightPanelTab.value = 'property';
      syncToolbarOperationState(kind, {
        name: kind === 'fillet' ? '圆角' : '倒角',
        hint: kind === 'fillet' ? '请选择要圆角化的边线、面或顶点。' : '请选择要倒角化的边线、面或顶点。',
      });
      return;
    }
    if (activeOperationCommand.value === 'fillet' || activeOperationCommand.value === 'chamfer') {
      syncToolbarOperationState(null, null);
    }
  });

  watch([() => revolveUiState.value.active], ([active]) => {
    if (active) {
      rightPanelTab.value = 'property';
      syncToolbarOperationState('revolve-boss', {
        name: '旋转凸台/基体',
        hint: '请选择旋转轴与同一草图中的闭合轮廓。',
      });
      return;
    }
    if (activeOperationCommand.value === 'revolve-boss') {
      syncToolbarOperationState(null, null);
    }
  });

  watch([() => revolveCutUiState.value.active], ([active]) => {
    if (active) {
      rightPanelTab.value = 'property';
      syncToolbarOperationState('revolve-cut', {
        name: '旋转切除',
        hint: '请选择旋转轴与同一草图中的闭合轮廓。',
      });
      return;
    }
    if (activeOperationCommand.value === 'revolve-cut') {
      syncToolbarOperationState(null, null);
    }
  });


  /**
   * 清理修剪工作流状态。
   *
   * @param input 清理选项。
   */
  const clearSketchTrimWorkflow = (input: ClearSketchTrimWorkflowInput = {}): void => {
    const { clearToolbarState = true, clearSelection = true } = input;
    sketchTrimMode.value = null;
    selectedSketchTrimEntity.value = null;
    sketchTrimOptions.value = { ...defaultSketchTrimOptions };
    const viewer = getViewer();
    viewer?.clearSketchTrimMode?.(false);
    /**
     * 默认退出修剪工作流时同步清空选择与 hover 高亮。
     * 恢复已有草图编辑属于例外：设计树动作会在恢复成功后选中目标草图节点，
     * 这里不能提前发布清空选择事件，否则会把后续端点拖动依赖的草图选中链路打断。
     */
    if (clearSelection) {
      viewer?.clearSelectionProgrammatically?.();
    }
    viewer?.setProgrammaticHover?.(null);
    if (!clearToolbarState) {
      return;
    }
    const activeCommand = activeOperationCommand.value;
    if (activeCommand !== null && isSketchTrimCommand(activeCommand)) {
      syncToolbarOperationState(null, null);
    }
  };

  /**
   * 清理 Convert Entities 工作流状态。
   *
   * @param input 清理选项。
   */
  const clearConvertEntitiesWorkflow = (input: { clearToolbarState?: boolean } = {}): void => {
    const { clearToolbarState = true } = input;
    if (convertEntitiesState.value.active) {
      cancelConvertEntities?.();
    }
    if (!clearToolbarState) {
      return;
    }
    if (activeOperationCommand.value === 'sketch-convert-entities') {
      syncToolbarOperationState(null, null);
    }
  };

  //------ 草图实体变换 UI 工作流 开始------
  /**
   * 恢复草图实体变换的前端父状态。
   *
   * @param command 正在收口的变换命令。
   * @param clearToolbarState 是否同步清理工具栏活动命令。
   */
  const resetSketchEntitiesTransformUiState = (
    command: SketchEntitiesTransformCommand | null,
    clearToolbarState: boolean,
  ): void => {
    if (isSketchEntitiesTransformCommand(command)) {
      /**
       * Viewer 模式不能反向修改 web-client 的 Pinia：
       * 1. 实体变换命令结束后，其父状态始终是当前活动草图的普通 Sketch 模式；
       * 2. Ctrl+C/V 以 Store 模式作为快捷键门禁，旧命令态必须在前端独立收口。
       */
      sketchStateStore.setViewerMode(ViewerMode.Sketch);
    }

    sketchEntitiesTransformState.value = createDefaultSketchEntitiesTransformState(null);
    if (
      clearToolbarState
      && isSketchEntitiesTransformCommand(activeOperationCommand.value)
    ) {
      syncToolbarOperationState(null, null);
    }
  };

  /**
   * 清理草图实体变换 UI 工作流。
   *
   * @param input 清理选项。
   */
  const clearSketchEntitiesTransformWorkflow = (input: { clearToolbarState?: boolean } = {}): void => {
    const { clearToolbarState = true } = input;
    const viewer = getViewer();
    const command = sketchEntitiesTransformState.value.command;

    try {
      if (command === SketchEntitiesTransformCommand.Move) {
        viewer?.clearSketchMoveEntitiesMode?.();
      } else if (command === SketchEntitiesTransformCommand.Copy) {
        viewer?.clearSketchCopyEntitiesMode?.();
      } else if (command === SketchEntitiesTransformCommand.Rotate) {
        viewer?.clearSketchRotateEntitiesMode?.();
      } else if (command === SketchEntitiesTransformCommand.Mirror) {
        viewer?.clearSketchMirrorEntitiesMode?.();
      }
    } finally {
      // 即使底层清理失败，UI 也不能残留一个已经无法交互的活动命令。
      resetSketchEntitiesTransformUiState(command, clearToolbarState);
    }
  };
  //------ 草图实体变换 UI 工作流 结束------

  //------ 圆周草图阵列 UI 工作流 开始------
  /**
   * 判断圆周草图阵列工作流是否激活。
   *
   * @returns 当前命令为圆周草图阵列时返回 true。
   */
  const isSketchCircularPatternWorkflowActive = (): boolean => {
    return activeOperationCommand.value === 'sketch-circular-pattern';
  };

  /**
   * 清理圆周草图阵列工作流。
   *
   * @param input 清理选项。
   */
  const clearSketchCircularPatternWorkflow = (input: { clearToolbarState?: boolean } = {}): void => {
    const { clearToolbarState = true } = input;
    if (!isSketchCircularPatternWorkflowActive()) {
      // 通用命令切换会调用所有清理入口；未激活的阵列命令不得把其它模式误写回 Sketch。
      return;
    }
    getViewer()?.clearSketchCircularPatternMode?.();
    sketchStateStore.setViewerMode(ViewerMode.Sketch);
    if (clearToolbarState) {
      syncToolbarOperationState(null, null);
    }
  };
  //------ 圆周草图阵列 UI 工作流 结束------

  /**
   * 判断当前 Viewer 是否存在真实活动草图会话。
   *
   * @returns 是否存在活动草图会话。
   */
  const hasActiveViewerSketchSession = (): boolean => {
    const viewer = getViewer();
    if (!viewer) {
      return false;
    }
    return Boolean(viewer.getActiveSketchGroupId?.());
  };

  /**
   * 清理与当前 Viewer 脱节的草图运行时状态。
   */
  const clearDetachedSketchRuntimeState = (): void => {
    const viewer = getViewer();
    // 旧项目残留的 Pinia 状态不能参与新 Viewer 的命令判断，否则“草图绘制”会被误判成退出/取消。
    currentDrawingTool.value = null;
    currentDrawingCommand.value = null;
    currentSketchGroupId.value = null;
    sketchStateStore.clearPendingSketchToolCommand();
    planeStore.clearSelectedDatumPlanePreview(viewer);
    currentDrawingDatumPlaneId.value = -1;
    selectedDatumPlaneId.value = -1;
    drawingStore.stopDrawingHintTimer();
    clearSketchTrimWorkflow({ clearToolbarState: false });
    clearConvertEntitiesWorkflow({ clearToolbarState: false });
    clearSketchEntitiesTransformWorkflow({ clearToolbarState: false });
    clearSketchCircularPatternWorkflow({ clearToolbarState: false });
    clearSketchOffsetWorkflow({ clearToolbarState: false, resetViewerMode: false });
    sketchCornerEnable.value = false;
    viewer?.clearSketchCornerMode?.(false);
    syncToolbarOperationState(null, null);
  };

  /**
   * 从属性面板确认 Convert Entities 并退出命令态。
   * 说明：
   * 1. geometry workflow 只负责提交与会话内状态回收；
   * 2. 草图命令态 `activeOperationCommand` 属于宿主工作流职责，因此确认成功后必须由这里统一收口；
   * 3. 若提交失败，则保持当前命令态不变，便于用户继续修正输入。
   */
  const confirmConvertEntitiesWorkflow = async (): Promise<void> => {
    const confirmed = await Promise.resolve(confirmExternalConvertEntities?.() ?? false);
    if (!confirmed) {
      return;
    }

    clearConvertEntitiesWorkflow();
    requestDesignTreeAfterSketchCommand(
      EditorPanelShowDesignTreeReason.CommandConfirmed,
      'sketch-convert-entities',
    );
  };

  /**
   * 从属性面板设置修剪模式。
   *
   * @param mode 目标修剪模式。
   */
  const setSketchTrimModeFromProperty = (mode: SketchTrimModeType | null): void => {
    if (!mode) {
      clearSketchTrimWorkflow();
      return;
    }

    sketchStateStore.setViewerMode(ViewerMode.SketchTrim);
    sketchTrimMode.value = mode;
    sketchTrimOptions.value = {
      ...sketchTrimOptions.value,
      mode,
    };

    const activeCommand = activeOperationCommand.value;
    const resolvedCommand = activeCommand !== null && isSketchTrimCommand(activeCommand)
      ? activeCommand
      : SketchTrimCommand.TrimEntities;
    const config = resolveSketchTrimCommandConfig(resolvedCommand);
    syncToolbarOperationState(resolvedCommand, {
      name: config.name,
      hint: config.hint,
    });

    const viewer = requireViewer();
    if (!viewer.isSketchTrimModeActive?.()) {
      viewer.startSketchTrimMode?.(mode, sketchTrimOptions.value);
    } else {
      viewer.setSketchTrimMode?.(mode);
      viewer.setSketchTrimOptions?.(sketchTrimOptions.value);
    }
  };

  /**
   * 从属性面板设置修剪选项。
   *
   * @param nextOptions 新修剪选项。
   */
  const setSketchTrimOptionsFromProperty = (nextOptions: SketchTrimOptionsType): void => {
    sketchStateStore.setViewerMode(ViewerMode.SketchTrim);
    sketchTrimOptions.value = { ...nextOptions };
    sketchTrimMode.value = nextOptions.mode;
    const viewer = requireViewer();
    if (!viewer.isSketchTrimModeActive?.()) {
      viewer.startSketchTrimMode?.(nextOptions.mode, nextOptions as unknown as Record<string, unknown>);
    } else {
      viewer.setSketchTrimOptions?.(nextOptions as unknown as Record<string, unknown>);
    }
  };

  //------ 智能尺寸命令 开始------
  /**
   * 尺寸命令家族变体表（阶段 8：相邻命令共用智能尺寸状态机底座）。
   *
   * 说明：key 即工具栏命令标识；options 透传 cad-core 控制器启动选项。
   */
  const SMART_DIMENSION_COMMAND_VARIANTS: Record<string, {
    name: string;
    hint: string;
    options?: {
      forcedOrientation?: 'horizontal' | 'vertical';
      pathLength?: boolean;
      sequence?: 'chain' | 'baseline' | 'ordinate';
    };
  }> = {
    'sketch-smart-dimension': {
      name: '智能尺寸',
      hint: '依次选择草图实体，移动指针切换候选方向，右键锁定，单击空白处放置，输入数值确认',
    },
    'sketch-horizontal-dimension': {
      name: '水平尺寸',
      hint: '选择两点或一条线，单击空白处放置水平距离尺寸',
      options: { forcedOrientation: 'horizontal' },
    },
    'sketch-vertical-dimension': {
      name: '竖直尺寸',
      hint: '选择两点或一条线，单击空白处放置竖直距离尺寸',
      options: { forcedOrientation: 'vertical' },
    },
    'sketch-path-length-dimension': {
      name: '路径长度',
      hint: '依次选择端到端相连的线段链，单击空白处放置路径总长尺寸',
      options: { pathLength: true },
    },
    'sketch-chain-dimension': {
      name: '链尺寸',
      hint: '依次选择相邻点，每相邻一对自动生成从动尺寸（方向继承首对），Esc 结束',
      options: { sequence: 'chain' },
    },
    'sketch-baseline-dimension': {
      name: '基准尺寸',
      hint: '先选择共享基准点，再依次选择引用，自动生成驱动尺寸（过定义时转从动），Esc 结束',
      options: { sequence: 'baseline' },
    },
    'sketch-ordinate-dimension': {
      name: '尺寸链',
      hint: '先选择基准点，再依次选择其他点，自动生成相对基准的从动坐标尺寸，Esc 结束',
      options: { sequence: 'ordinate' },
    },
    'sketch-horizontal-ordinate-dimension': {
      name: '水平尺寸链',
      hint: '先选择基准点，再依次选择其他点，自动生成相对基准的水平从动坐标尺寸，Esc 结束',
      options: { sequence: 'ordinate', forcedOrientation: 'horizontal' },
    },
    'sketch-vertical-ordinate-dimension': {
      name: '竖直尺寸链',
      hint: '先选择基准点，再依次选择其他点，自动生成相对基准的竖直从动坐标尺寸，Esc 结束',
      options: { sequence: 'ordinate', forcedOrientation: 'vertical' },
    },
  };

  /** 判断命令是否属于尺寸命令家族。 */
  const isSmartDimensionFamilyCommand = (commandType: string): boolean =>
    commandType in SMART_DIMENSION_COMMAND_VARIANTS;

  /**
   * 启动尺寸命令家族（智能尺寸及阶段 8 相邻命令变体）。
   *
   * 设计说明：
   * 1. 独占命令会话：进入前终止绘制态，避免选择处理器把点击让给绘制工具；
   * 2. 多阶段选择/预览/锁定/放置状态机全部在 cad-core 控制器，
   *    这里只负责模式切换与工具栏状态同步。
   *
   * @param commandType 家族命令标识；省略为标准智能尺寸。
   */
  const startSmartDimensionWorkflow = async (commandType = 'sketch-smart-dimension'): Promise<void> => {
    const variant = SMART_DIMENSION_COMMAND_VARIANTS[commandType];
    if (!variant) {
      return;
    }
    if (!await ensureSketchCommandContext(commandType)) {
      return;
    }
    const viewer = requireViewer();
    if (isDrawingMode.value) {
      viewer.stopSketchDrawing();
      currentDrawingTool.value = null;
    }
    smartDimensionActive.value = true;
    sketchStateStore.setViewerMode(ViewerMode.SketchDimension);
    syncToolbarOperationState(commandType, {
      name: variant.name,
      hint: variant.hint,
    });
    viewer.startSketchSmartDimensionMode?.(variant.options);
  };

  /**
   * 停止尺寸命令家族并回到草图态。
   */
  const stopSmartDimensionWorkflow = (): void => {
    if (!smartDimensionActive.value) {
      return;
    }
    smartDimensionActive.value = false;
    getViewer()?.cancelSketchSmartDimensionMode?.();
    sketchStateStore.setViewerMode(ViewerMode.Sketch);
    if (isSmartDimensionFamilyCommand(activeOperationCommand.value ?? '')) {
      syncToolbarOperationState(null, null);
    }
  };
  //------ 智能尺寸命令 结束------

  /**
   * 确保草图命令具备基准面与会话上下文。
   *
   * @param commandType 命令标识。
   * @returns 是否具备上下文。
   */
  const ensureSketchCommandContext = async (commandType: string): Promise<boolean> => {
    if (selectedDatumPlaneId.value !== -1) {
      currentDrawingDatumPlaneId.value = selectedDatumPlaneId.value;
    }

    if (isSketchEditMode.value && !hasActiveViewerSketchSession()) {
      clearDetachedSketchRuntimeState();
      pendingSketchToolCommand.value = commandType;
      enterSketchArmedState({
        message: '请先选择前视/上视/右视基准面或平面面',
        level: 'warning',
      });
      return false;
    }

    if (currentDrawingDatumPlaneId.value === -1) {
      const selectedPlanarFace = getViewer()?.getSelectedPlanarFace?.() ?? null;
      if (selectedPlanarFace) {
        if (!selectedPlanarFace.isPlanar) {
          message.warning('当前仅支持在平面面上进入2D草图');
          return false;
        }
        await enterSketchEditingOnPlanarFace(selectedPlanarFace);
        return currentDrawingDatumPlaneId.value !== -1;
      }
      pendingSketchToolCommand.value = commandType;
      enterSketchArmedState({
        message: '请先选择前视/上视/右视基准面或平面面',
        level: 'warning',
      });
      return false;
    }

    if (!isSketchEditMode.value) {
      const sketchGroupId = await startSketchSession(currentDrawingDatumPlaneId.value);
      if (!sketchGroupId) {
        clearDetachedSketchRuntimeState();
        pendingSketchToolCommand.value = commandType;
        enterSketchArmedState({
          message: '请先选择前视/上视/右视基准面或平面面',
          level: 'warning',
        });
        return false;
      }
      sketchStateStore.setViewerMode(ViewerMode.Sketch);
    }

    return true;
  };

  /**
   * 激活修剪工作流。
   *
   * @param commandType 修剪命令。
   */
  const activateSketchTrimWorkflow = async (commandType: SketchTrimCommand): Promise<void> => {
    if (!await ensureSketchCommandContext(commandType)) {
      return;
    }

    //------ 进入裁剪实体前清空选择 开始------
    /**
     * 进入裁剪实体会话前必须清空当前全部选中态：
     * 1. 防止线条/点等草图属性面板继续命中可见条件；
     * 2. 避免旧选择高亮干扰裁剪路径反馈；
     * 3. 统一通过选择中心清空，保证场景与状态仓库一致。
     */
    getViewer()?.clearSelectionProgrammatically?.();
    //------ 进入裁剪实体前清空选择 结束------

    //------ 裁剪模式切换竞态收口 开始------
    // 必须先终止绘图，再把宿主模式切到 SketchTrim。
    // 否则 stopSketchDrawing() 触发的完成回调会把 viewerMode 回写成 Sketch，
    // 导致选择层退回普通草图 handler，进而重新允许框选。
    getViewer()?.stopSketchDrawing?.();
    //------ 裁剪模式切换竞态收口 结束------

    sketchStateStore.clearPendingSketchToolCommand();
    sketchStateStore.setViewerMode(ViewerMode.SketchTrim);
    currentDrawingTool.value = null;
    currentDrawingCommand.value = null;
    drawingStore.stopDrawingHintTimer();
    sketchCornerEnable.value = false;
    getViewer()?.clearSketchCornerMode?.(false);

    const config = resolveSketchTrimCommandConfig(commandType);
    sketchTrimMode.value = config.mode;
    sketchTrimOptions.value = {
      ...sketchTrimOptions.value,
      mode: config.mode,
      singleEntityBehavior: config.singleEntityBehavior,
    };
    selectedSketchTrimEntity.value = null;
    //------ 修剪命令打开属性栏 开始------
    /**
     * 修剪命令进入后必须立即打开 PropertyManager：
     * 1. 修剪属性 section 已通过 `sketchTrimMode` 独占显示；
     * 2. 若右侧仍停留在设计树，用户无法看到强力剪裁选项和确认/取消按钮。
     */
    rightPanelTab.value = 'property';
    //------ 修剪命令打开属性栏 结束------

    syncToolbarOperationState(commandType, {
      name: config.name,
      hint: config.hint,
    });

    requireViewer().startSketchTrimMode?.(config.mode, sketchTrimOptions.value);
  };

  //------ 等距实体命令收口 开始------
  /**
   * 激活等距实体工作流。
   *
   * 说明：
   * 1. 确保已进入草图环境；
   * 2. 保留当前选中态：与 SolidWorks「先选中实体再点等距实体」一致，
   *    命令激活前已在普通草图选择链中选中的草图实体由 cad-core 控制器在
   *    startMode 阶段自动吸收为偏移源实体（此时不清空选择）；
   * 3. 将 Viewer 切换到 SketchOffset 模式；
   * 4. 通过 Viewer 门面启动偏移控制器。
   */
  const activateSketchOffsetWorkflow = async (): Promise<void> => {
    if (!await ensureSketchCommandContext('sketch-offset-entities')) {
      return;
    }

    getViewer()?.stopSketchDrawing?.();

    sketchStateStore.clearPendingSketchToolCommand();
    sketchStateStore.setViewerMode(ViewerMode.SketchOffset);
    currentDrawingTool.value = null;
    currentDrawingCommand.value = null;
    drawingStore.stopDrawingHintTimer();

    rightPanelTab.value = 'property';

    syncToolbarOperationState('sketch-offset-entities', {
      name: '等距实体',
      hint: '选择草图实体后设置偏移距离',
    });

    requireViewer().startSketchOffsetMode?.({
      distance: sketchOffsetDistance.value,
      isReverse: sketchOffsetReverse.value,
      isBidirectional: sketchOffsetBidirectional.value,
      isAddSize: sketchOffsetAddSize.value,
      isOpenSelectChain: sketchOffsetSelectChain.value,
      cover: sketchOffsetCover.value,
      isBasicGeometryBody: sketchOffsetBasicGeometry.value,
      isOffsetGeometryBody: sketchOffsetOffsetGeometry.value,
    });
  };

  /**
   * 生成等距实体并退出
   */
  const generateAndExitSketchOffsetWorkflow = (): void => {
    getViewer().generateEquidistantEntities();
    clearSketchOffsetWorkflow();
  }

  /**
   * 判断当前是否处于等距实体工作流。
   *
   * @returns 是否处于等距实体工作流。
   */
  const isSketchOffsetWorkflowActive = (): boolean => {
    return activeOperationCommand.value === 'sketch-offset-entities';
  };

  /**
   * 清除等距实体工作流。
   *
   * @param input 清理选项。
   */
  const clearSketchOffsetWorkflow = (
    input: { clearToolbarState?: boolean; resetViewerMode?: boolean } = {},
  ): void => {
    const { clearToolbarState = true, resetViewerMode = true } = input;
    // 采用可选调用：与修剪/变换清理一致，允许在 Viewer 可能未就绪的脱节清理场景中复用。
    getViewer()?.clearSketchOffsetMode?.();
    if (resetViewerMode) {
      sketchStateStore.setViewerMode(ViewerMode.Sketch);
    }
    currentDrawingTool.value = null;
    currentDrawingCommand.value = null;
    if (clearToolbarState && isSketchOffsetWorkflowActive()) {
      syncToolbarOperationState(null, null);
    }
    rightPanelTab.value = 'feature';
  };

  /**
   * 从属性面板设置等距实体距离。
   */
  const setSketchOffsetDistanceFromProperty = (value: number): void => {
    sketchOffsetDistance.value = value;
    // 仅更新参数并刷新预览，不重置已选实体
    requireViewer().updateSketchOffsetOptions?.({ distance: value });
  };

  /**
   * 从属性面板设置等距实体方向。
   *
   * 说明：双向偏移时反向无意义（与 SolidWorks 一致），
   * 反向选择框在 UI 层已禁用，此处再作防御性拦截，
   * 确保双向开启期间不会出现"双向 + 反向"的 UI 镜像不一致。
   */
  const setSketchOffsetReverseFromProperty = (value: boolean): void => {
    if (sketchOffsetBidirectional.value) return;
    sketchOffsetReverse.value = value;
    requireViewer().updateSketchOffsetOptions?.({ isReverse: value });
  };

  /**
   * 从属性面板设置等距实体双向。
   *
   * 说明：勾选双向时同步复位反向（内核在 updateOptions 中也会强制复位），
   * 保证属性面板 UI 状态与内核状态一致。
   */
  const setSketchOffsetBidirectionalFromProperty = (value: boolean): void => {
    sketchOffsetBidirectional.value = value;
    if (value) {
      sketchOffsetReverse.value = false;
    }
    requireViewer().updateSketchOffsetOptions?.({ isBidirectional: value });
  };

  /**
   * 从属性面板设置等距实体添加尺寸。
   */
  const setSketchOffsetAddSizeFromProperty = (value: boolean): void => {
    sketchOffsetAddSize.value = value;
    requireViewer().updateSketchOffsetOptions?.({ isAddSize: value });
  };

  /**
   * 从属性面板设置等距实体选择链。
   */
  const setSketchOffsetSelectChainFromProperty = (value: boolean): void => {
    sketchOffsetSelectChain.value = value;
    requireViewer().updateSketchOffsetOptions?.({ isOpenSelectChain: value });
  };

  /**
   * 从属性面板设置等距实体盖子。
   */
  const setSketchOffsetCoverFromProperty = (value: { hasCover: boolean; type: CoverType }): void => {
    sketchOffsetCover.value = value;
    requireViewer().updateSketchOffsetOptions?.({ cover: value });
  };

  /**
   * 从属性面板设置等距实体基本几何体。
   */
  const setSketchOffsetBasicGeometryFromProperty = (value: boolean): void => {
    sketchOffsetBasicGeometry.value = value;
    requireViewer().updateSketchOffsetOptions?.({ isBasicGeometryBody: value });
  };

  /**
   * 从属性面板设置等距实体偏移几何体。
   */
  const setSketchOffsetOffsetGeometryFromProperty = (value: boolean): void => {
    sketchOffsetOffsetGeometry.value = value;
    requireViewer().updateSketchOffsetOptions?.({ isOffsetGeometryBody: value });
  };
  //------ 等距实体命令收口 结束------

  //------ 草图圆角命令收口 开始------
  /**
   * 激活草图圆角/倒角工作流。
   *
   * @param commandType 草图圆角/倒角命令。
   */
  const activateSketchCornerWorkflow = async (commandType: SketchCornerMode): Promise<void> => {
    if (!await ensureSketchCommandContext(commandType)) {
      return;
    }

    const viewer = requireViewer();

    /**
     * 草图圆角/倒角是草图内独占命令，不属于普通绘制工具：
     * 1. 进入前终止绘制态，避免旧绘制 handler 继续消费视口点击；
     * 2. 统一清理修剪与转换实体会话，保证 PropertyManager 只显示当前命令；
     * 3. 将 toolbar、PropertyManager 与 cad-core 控制器全部从这里同步，避免顶层分发和草图分发重复处理。
     */
    viewer.stopSketchDrawing?.();
    clearSketchTrimWorkflow({ clearToolbarState: false });
    clearConvertEntitiesWorkflow({ clearToolbarState: false });
    clearSketchEntitiesTransformWorkflow({ clearToolbarState: false });
    clearSketchCircularPatternWorkflow({ clearToolbarState: false });
    sketchStateStore.clearPendingSketchToolCommand();
    sketchStateStore.setViewerMode(ViewerMode.Sketch);
    selectedSketchTrimEntity.value = null;
    rightPanelTab.value = 'property';
    currentDrawingTool.value = null;
    currentDrawingCommand.value = null;
    drawingStore.stopDrawingHintTimer();
    sketchCornerMode.value = commandType;
    sketchCornerEnable.value = true;

    const config = sketchCornerCommandConfig[commandType];
    syncToolbarOperationState(commandType, {
      name: config.name,
      hint: config.hint,
    });

    viewer.startSketchCornerMode?.(commandType);
  };
  //------ 草图圆角命令收口 结束------

  //------ 草图实体变换 UI 工作流 开始------
  /**
   * 激活草图实体变换 UI 工作流。
   *
   * @param commandType 草图实体变换命令。
   */
  const activateSketchEntitiesTransformWorkflow = async (
    commandType: SketchEntitiesTransformCommand,
  ): Promise<void> => {
    if (
      commandType === SketchEntitiesTransformCommand.Mirror
      && (!isSketchEditMode.value || !hasActiveViewerSketchSession())
    ) {
      message.warning('请先进入草图编辑状态，再使用镜向实体');
      return;
    }
    if (!await ensureSketchCommandContext(commandType)) {
      return;
    }

    if (
      commandType === SketchEntitiesTransformCommand.Mirror
      && sketchStateStore.viewerMode === ViewerMode.SketchOffset
    ) {
      // 镜向控制器只接受普通草图态；先走等距实体统一出口，确保 Viewer 与 Pinia 同步回到 Sketch。
      clearSketchOffsetWorkflow();
    }

    const viewer = requireViewer();
    viewer.stopSketchDrawing?.();
    //------ 移动实体预选实体吸收 开始------
    /**
     * SolidWorks 支持“先选中实体，再点击移动实体”。
     * 这里必须在清空普通选择前保存对象快照，后续交给 cad-core 控制器统一解析为可变换草图实体。
     */
    const preselectedSketchTransformObjects = viewer.getSelectedObjects?.() ?? [];
    //------ 移动实体预选实体吸收 结束------
    viewer.clearSelectionProgrammatically?.();
    clearSketchTrimWorkflow({ clearToolbarState: false });
    clearConvertEntitiesWorkflow({ clearToolbarState: false });
    clearSketchEntitiesTransformWorkflow({ clearToolbarState: false });
    clearSketchCircularPatternWorkflow({ clearToolbarState: false });
    sketchCornerEnable.value = false;
    viewer.clearSketchCornerMode?.(false);
    sketchStateStore.clearPendingSketchToolCommand();
    const viewerMode = resolveSketchEntitiesTransformViewerMode(commandType);
    selectedSketchTrimEntity.value = null;
    currentDrawingTool.value = null;
    currentDrawingCommand.value = null;
    drawingStore.stopDrawingHintTimer();
    sketchEntitiesTransformState.value = createDefaultSketchEntitiesTransformState(commandType);

    if (commandType === SketchEntitiesTransformCommand.Move) {
      sketchStateStore.setViewerMode(viewerMode);
      viewer.startSketchMoveEntitiesMode?.({ preselectedObjects: preselectedSketchTransformObjects });
    } else if (commandType === SketchEntitiesTransformCommand.Copy) {
      sketchStateStore.setViewerMode(viewerMode);
      viewer.startSketchCopyEntitiesMode?.({ preselectedObjects: preselectedSketchTransformObjects });
    } else if (commandType === SketchEntitiesTransformCommand.Rotate) {
      sketchStateStore.setViewerMode(viewerMode);
      viewer.startSketchRotateEntitiesMode?.({ preselectedObjects: preselectedSketchTransformObjects });
    } else if (commandType === SketchEntitiesTransformCommand.Mirror) {
      let started = false;
      try {
        started = viewer.startSketchMirrorEntitiesMode?.({
          preselectedObjects: preselectedSketchTransformObjects,
        }) ?? false;
      } catch (error) {
        // Viewer 已保证启动事务原子回滚；这里只恢复前端状态，避免重复清理底层控制器。
        resetSketchEntitiesTransformUiState(commandType, true);
        throw error;
      }
      if (!started) {
        resetSketchEntitiesTransformUiState(commandType, true);
        message.warning('当前草图无法启动镜向实体');
        return;
      }
      sketchStateStore.setViewerMode(viewerMode);
    }

    const name = resolveSketchEntitiesTransformName(commandType);
    syncToolbarOperationState(commandType, {
      name,
      hint: `${name}：请选择要处理的草图实体并设置参数。`,
    });
    /**
     * 控制器启动时会清空普通选择，空选订阅可能同步请求显示设计树。
     * 必须在 Viewer 与命令状态都成功激活后最后切换属性栏，保证 PropertyManager 不被启动竞态覆盖。
     */
    rightPanelTab.value = 'property';
  };
  //------ 草图实体变换 UI 工作流 结束------

  //------ 圆周草图阵列 UI 工作流 开始------
  /**
   * 激活圆周草图阵列工作流。
   */
  const activateSketchCircularPatternWorkflow = async (): Promise<void> => {
    const commandType = 'sketch-circular-pattern';
    if (!await ensureSketchCommandContext(commandType)) {
      return;
    }

    const viewer = requireViewer();
    viewer.stopSketchDrawing?.();
    const preselectedObjects = viewer.getSelectedObjects?.() ?? [];
    viewer.clearSelectionProgrammatically?.();
    clearSketchTrimWorkflow({ clearToolbarState: false });
    clearConvertEntitiesWorkflow({ clearToolbarState: false });
    clearSketchEntitiesTransformWorkflow({ clearToolbarState: false });
    clearSketchCircularPatternWorkflow({ clearToolbarState: false });
    sketchCornerEnable.value = false;
    viewer.clearSketchCornerMode?.(false);
    sketchStateStore.clearPendingSketchToolCommand();
    sketchStateStore.setViewerMode(ViewerMode.SketchCircularPattern);
    selectedSketchTrimEntity.value = null;
    rightPanelTab.value = 'property';
    currentDrawingTool.value = null;
    currentDrawingCommand.value = null;
    drawingStore.stopDrawingHintTimer();
    syncToolbarOperationState(commandType, {
      name: '圆周阵列',
      hint: '选择要阵列的草图实体，并设置中心、角度和实例数。',
    });
    viewer.startSketchCircularPatternMode?.({ preselectedObjects });
  };

  /**
   * 确认圆周草图阵列并退出命令态。
   */
  const confirmSketchCircularPatternWorkflow = (): void => {
    const confirmed = requireViewer().confirmSketchCircularPattern?.() ?? false;
    if (!confirmed) {
      return;
    }
    clearSketchCircularPatternWorkflow();
    requestDesignTreeAfterSketchCommand(
      EditorPanelShowDesignTreeReason.CommandConfirmed,
      'sketch-circular-pattern',
    );
  };

  /**
   * 取消圆周草图阵列并退出命令态。
   */
  const cancelSketchCircularPatternWorkflow = (): void => {
    getViewer()?.cancelSketchCircularPattern?.();
    clearSketchCircularPatternWorkflow();
    requestDesignTreeAfterSketchCommand(
      EditorPanelShowDesignTreeReason.CommandCanceled,
      'sketch-circular-pattern',
    );
  };
  //------ 圆周草图阵列 UI 工作流 结束------

  /**
   * 设置草图圆角/倒角模式。
   *
   * @param mode 目标模式。
   */
  const setSketchCornerModeFromProperty = (mode: SketchCornerMode | null): void => {
    if (!mode) {
      sketchCornerEnable.value = false;
      getViewer()?.clearSketchCornerMode?.(false);
      if (activeOperationCommand.value !== null && isSketchCornerMode(activeOperationCommand.value)) {
        syncToolbarOperationState(null, null);
      }
      return;
    }

    void activateSketchCornerWorkflow(mode);
  };

  /**
   * 进入待选草图宿主的 armed 状态。
   *
   * @param input 提示选项。
   */
  const enterSketchArmedState = (input: { message?: string; level?: 'info' | 'warning' } = {}): void => {
    const viewer = getViewer();
    if (!viewer) {
      message.error('视口未初始化，无法选择草图平面');
      return;
    }
    sketchStateStore.setViewerMode(idleViewerMode.value);
    currentDrawingDatumPlaneId.value = -1;
    selectedDatumPlaneId.value = -1;
    planeStore.captureDefaultPlaneSnapshot(viewer);
    const visibleDefaultPlaneCount = planeStore.showDefaultDatumPlanes(viewer);
    const hint = input.message ?? '请选择前视/上视/右视基准面或平面面以开始草图';
    syncToolbarOperationState('sketch-enter', {
      name: '草图绘制',
      hint,
    });
    let shouldEmitPrompt = true;
    if (visibleDefaultPlaneCount === 0) {
      message.warning('未找到默认基准面，请选择模型平面面以开始草图');
      shouldEmitPrompt = false;
    }
    if (input.level === 'warning' && shouldEmitPrompt) {
      message.warning(hint);
      return;
    }
    if (shouldEmitPrompt) {
      message.info(hint);
    }
  };

  /**
   * 退出 armed 状态。
   */
  const exitSketchArmedState = (): void => {
    const viewer = getViewer();
    sketchStateStore.setViewerMode(idleViewerMode.value);
    sketchStateStore.clearPendingSketchToolCommand();
    planeStore.restoreDefaultPlaneSnapshot(undefined, viewer);
    planeStore.clearDefaultPlaneSnapshot();
    syncToolbarOperationState(null, null);
  };

  /**
   * 写入草图编辑会话的公共宿主上下文。
   *
   * 设计说明：
   * 1. start (on datum plane / planar face) 与 resume 三条链路会写入相同的一组 store + viewer mode；
   * 2. 默认基准面隐藏 / 基准面快照 / 相机进入草图视角在三条链路里各不相同，仍由调用方自行处理，
   *    避免本 helper 固化顺序而破坏原有行为；
   * 3. `sketchGroupId` 允许为 null / undefined，以兼容 `beginSketchOnPlanarFace` 返回结果里 `sketchId`
   *    可能尚未就绪的历史形态；缺省时不写 `currentSketchGroupId`，其余上下文仍然一致写入。
   *
   * @param input 会话上下文参数。
   */
  const applySketchEditingSessionContext = (input: {
    sketchGroupId: string | null | undefined;
    planeId: number;
  }): void => {
    const viewer = getViewer();

    /**
     * 草图编辑会话态必须在同一个出口同步到 Pinia、Viewer 与工具栏命令 Store。
     * 右键“编辑草图”不会经过顶部按钮命令分发，如果只更新其中一处，
     * 顶部工具栏会出现已切到草图 tab 但入口仍停留在“草图绘制”的分裂状态。
     */
    sketchStateStore.setViewerMode(ViewerMode.Sketch);
    viewer?.setMode(ViewerMode.Sketch);
    toolbarCommandStore.setActiveCommands(['sketch-enter']);
    currentDrawingDatumPlaneId.value = input.planeId;
    selectedDatumPlaneId.value = input.planeId;
    if (input.sketchGroupId) {
      currentSketchGroupId.value = input.sketchGroupId;
    }
  };

  /**
   * 在基准面上进入草图编辑。
   *
   * @param planeId 基准面 ID。
   * @param planeName 基准面名称。
   */
  const enterSketchEditingOnDatumPlane = async (planeId: number, planeName?: string): Promise<void> => {
    const viewer = getViewer();
    const plane = viewer?.getDatumPlaneSummary(planeId) ?? null;
    if (!viewer || !plane) {
      return;
    }
    // 只有真实草图分组创建成功后才写入编辑态，避免 UI 状态早于 Viewer 会话。
    const sketchGroupId = await startSketchSession(planeId);
    if (!sketchGroupId) {
      message.warning('未能基于当前基准面启动草图');
      clearDetachedSketchRuntimeState();
      return;
    }
    applySketchEditingSessionContext({ sketchGroupId, planeId });
    planeStore.captureDefaultPlaneSnapshot(viewer);
    if (isDefaultDatumPlaneSummary(plane)) {
      planeStore.rememberSketchPlaneVisibility(planeId, viewer);
    }
    viewer.enterDatumPlaneView(planeId);
    planeStore.hideDefaultDatumPlanes(viewer);
    if (planeName) {
      message.info(`已进入草图环境(${planeName})，请选择草图工具`);
      return;
    }
    message.info('已进入草图环境，请选择草图工具');
  };

  /**
   * 在平面面上进入草图编辑。
   *
   * @param face 平面面选择快照。
   */
  const enterSketchEditingOnPlanarFace = async (face: SketchPlanarFaceSelection): Promise<void> => {
    const viewer = getViewer();
    if (!viewer) {
      return;
    }

    const hadActiveSketchSession = Boolean(viewer.getActiveSketchGroupId?.());
    const started = viewer.beginSketchOnPlanarFace?.({
      face,
      planeName: '草图面',
    }) ?? null;
    if (!started) {
      message.warning('未能基于当前选中平面面启动草图');
      return;
    }

    applySketchEditingSessionContext({
      sketchGroupId: started.sketchId,
      planeId: started.planeId,
    });
    if (!hadActiveSketchSession && started.sketchId) {
      await commitSketchSessionCreateHistory?.({
        sketchGroupId: started.sketchId,
        label: '进入草图',
        commandType: 'sketch.session.create',
      });
    }
    planeStore.captureDefaultPlaneSnapshot(viewer);
    planeStore.hideDefaultDatumPlanes(viewer);
    planeStore.restoreDefaultPlaneSnapshot(started.planeId, viewer);
    planeStore.clearDefaultPlaneSnapshot();
    //------ 特征面草图绘制朝正 开始------
    /**
     * 特征面草图必须复用 cad-core 在 beginSketchOnPlanarFace 中解析出的 viewFrame：
     * 1. viewFrame 会根据实体面法向、当前相机所在侧和当前相机 up 计算 SolidWorks 风格的 Normal To 姿态；
     * 2. 若这里降级为无参数 enterDatumPlaneView，会丢失特征面的相机朝向提示，导致屏幕旋转被普通基准面规则覆盖；
     * 3. 因此宿主层只做显式重放，不重新推导相机姿态。
     */
    viewer.enterDatumPlaneView(started.planeId, {
      cameraPosition: started.viewFrame.cameraPosition.clone(),
      target: started.viewFrame.target.clone(),
      up: started.viewFrame.up.clone(),
    });
    //------ 特征面草图绘制朝正 结束------
    message.info('已进入草图环境，请选择草图工具');
  };

  /**
   * 启动草图会话。
   *
   * @param planeId 基准面 ID。
   * @returns 草图分组 ID；启动失败时返回 null。
   */
  const startSketchSession = async (planeId: number): Promise<string | null> => {
    const viewer = getViewer();
    if (!viewer) {
      return null;
    }
    const hadActiveSketchSession = Boolean(viewer.getActiveSketchGroupId?.());
    const group = viewer.startSketchSession({ planeId });
    if (!group) {
      return null;
    }
    currentSketchGroupId.value = group.uuid;
    if (!hadActiveSketchSession) {
      await commitSketchSessionCreateHistory?.({
        sketchGroupId: group.uuid,
        label: '进入草图',
        commandType: 'sketch.session.create',
      });
    }
    return group.uuid;
  };

  /**
   * 在恢复已有草图编辑前清理互斥中的宿主工作流。
   *
   * 设计说明：
   * 1. 设计树右键“编辑草图”属于直接入口，不会经过 `handleCommand` 的统一互斥收口；
   * 2. 因此这里补一层最小宿主守卫，把 `extrude / revolve / blend / reference-*` 等互斥工作流先关闭；
   * 3. 仅清理“尚未进入草图编辑态”的冲突工作流；若已经在编辑其他草图，仍由 `resumeSketchEditing` 主逻辑拒绝切换。
   *
   * @returns 是否已满足恢复草图编辑的宿主前置条件。
   */
  const prepareHostStateForSketchEditingResume = (): boolean => {
    if (hasPendingSketchCommand.value && isIdleViewerModeActive.value) {
      exitSketchArmedState();
    }
    if (isReferenceFeatureCommand(activeOperationCommand.value)) {
      cancelReferenceFeatureCommand(activeOperationCommand.value);
    }
    if (isConvertEntitiesWorkflowActive()) {
      clearConvertEntitiesWorkflow();
    }
    if (isSketchEntitiesTransformWorkflowActive()) {
      clearSketchEntitiesTransformWorkflow();
    }
    if (isExtrudeMode.value) {
      cancelExtrude();
    }
    if (revolveUiState.value.active) {
      cancelRevolve();
    }
    if (revolveCutUiState.value.active) {
      cancelRevolveCut();
    }
    if (isBlendMode.value) {
      cancelBlend();
    }
    return true;
  };

  /**
   * 恢复草图编辑前收口 cad-core 中残留的活动草图。
   *
   * 设计说明：
   * 1. 设计树右键入口以目标草图为准，不能被 cad-core 内部残留的非目标活动草图静默阻断；
   * 2. 若 UI 已明确处于另一个草图编辑态，仍提示用户先退出，避免跨草图切换丢失用户正在编辑的上下文；
   * 3. 若 UI 并不处于草图态但底层仍残留活动草图，说明宿主状态与 cad-core 状态已经分裂，
   *    这里通过 `endSketchSession` 先释放底层会话，再恢复目标草图，保证右键入口不会无声失败。
   *
   * @param viewer 当前 Viewer。
   * @param targetSketchGroupId 目标草图组 ID。
   * @returns 是否允许继续恢复目标草图。
   */
  const releaseConflictingSketchSessionForResume = async (
    viewer: Viewer,
    targetSketchGroupId: string,
  ): Promise<boolean> => {
    const activeSketchId = viewer.getActiveSketchGroupId();
    if (!activeSketchId || activeSketchId === targetSketchGroupId) {
      return true;
    }
    if (isSketchEditMode.value) {
      message.warning('请先退出当前草图，再编辑其他草图');
      return false;
    }

    try {
      await viewer.endSketchSession?.({
        discardEmpty: true,
      });
    } catch (error) {
      if (!isStaleWorkerResponseError(error)) {
        throw error;
      }
    }
    currentSketchGroupId.value = null;
    currentDrawingTool.value = null;
    currentDrawingCommand.value = null;
    drawingStore.stopDrawingHintTimer();
    sketchStateStore.setViewerMode(idleViewerMode.value);
    return true;
  };

  /**
   * 恢复对已有草图的编辑。
   *
   * 设计说明：
   * 1. 与 `enterSketchEditingOnDatumPlane` 共享 `applySketchEditingSessionContext` 以避免两条链路
   *    各写一套宿主上下文；
   * 2. 即使当前目标草图已经活动，也必须重新经过 cad-core 恢复入口，
   *    用同一链路同步真实可见性、选择态与工具栏态；
   * 3. 已在编辑其他草图时拒绝切换，由用户先退出当前草图；
   * 4. 假设 `enterDatumPlaneView` / 默认基准面隐藏 / `rememberSketchPlaneVisibility` 不抛错；
   *    若实施期发现可抛，需在 store 写入前用 try/catch 并回滚已改状态。
   *
   * @param sketchGroupId 目标草图 uuid。
   * @returns 是否成功进入编辑。
   */
  const resumeSketchEditing = async (sketchGroupId: string): Promise<boolean> => {
    const viewer = getViewer();
    if (!viewer) {
      return false;
    }

    if (!await releaseConflictingSketchSessionForResume(viewer, sketchGroupId)) {
      return false;
    }
    if (!prepareHostStateForSketchEditingResume()) {
      return false;
    }

    if (isDrawingMode.value) {
      viewer.stopSketchDrawing?.();
      drawingStore.stopDrawingHintTimer();
    }
    const group = viewer.resumeSketchSession({ sketchGroupId });
    if (!group) {
      message.warning('草图未找到或当前已在编辑其他草图');
      return false;
    }

    //------ 已有草图编辑主选清理 开始------
    /**
     * 设计树右键编辑已有草图前，草图节点可能已经通过树选中链路写入 Viewer 主选。
     * 恢复编辑成功后必须先清掉这类残留主选，再让设计树只定位活动草图节点，
     * 否则进入已有草图会表现为草图内全部对象默认被选中。
     */
    viewer.clearSelectionProgrammatically?.();
    //------ 已有草图编辑主选清理 结束------

    const planeId = group.referencePlane.id;
    const plane = viewer.getDatumPlaneSummary(planeId);
    planeStore.captureDefaultPlaneSnapshot(viewer);
    if (plane && isDefaultDatumPlaneSummary(plane)) {
      planeStore.rememberSketchPlaneVisibility(planeId, viewer);
    }
    applySketchEditingSessionContext({ sketchGroupId: group.uuid, planeId });
    viewer.enterDatumPlaneView(planeId);
    planeStore.hideDefaultDatumPlanes(viewer);

    // 清理瞬时 UI 状态，避免 resume 后上一次离开前的临时命令 / 工具残留
    currentDrawingTool.value = null;
    currentDrawingCommand.value = null;
    clearSketchTrimWorkflow({ clearToolbarState: false, clearSelection: false });
    clearSketchEntitiesTransformWorkflow({ clearToolbarState: false });
    clearSketchCircularPatternWorkflow({ clearToolbarState: false });
    activateSketchEditingToolbarState();
    sketchStateStore.clearPendingSketchToolCommand();
    sketchCornerEnable.value = false;
    viewer.clearSketchCornerMode?.(false);

    message.info('已进入草图编辑');
    return true;
  };

  /**
   * 判断当前是否允许退出草图环境。
   *
   * @param input 退出草图的行为策略。
   * @returns 是否允许退出。
   */
  const canExitSketch = (input: ExitSketchModeInput = {}): boolean => {
    if (!getViewer()) {
      return true;
    }
    if (isDrawingMode.value && input.allowDrawingStop !== true) {
      // 手动退出不能隐式丢弃绘制态，避免用户半绘制内容被意外清掉。
      message.warning('请先完成或取消当前绘制');
      return false;
    }
    return true;
  };

  /**
   * PropertyManager 帮助占位行为。
   */
  const handlePropertyManagerHelp = (): void => {
    message.info('帮助文档尚未接入');
  };

  /**
   * PropertyManager 预览切换占位行为。
   */
  const handlePropertyManagerPreviewToggle = (): void => {
    message.info('预览切换暂未实现');
  };

  /**
   * 同步视口交互模式。
   */
  const syncViewportInteractionMode = (): void => {
    const viewer = getViewer();
    if (!viewer) {
      return;
    }
    viewer.setMode(viewerMode.value);
  };

  watch([viewerMode], () => {
    syncViewportInteractionMode();
  });

  /**
   * 处理 ESC 键。
   */
  const handleEscKey = (): void => {
    const viewer = getViewer();
    if (!viewer) {
      return;
    }
    //------ 智能尺寸命令 开始------
    /**
     * Esc 路由判据必须用 Viewer 控制器真相（快照 state），不能依赖 workflow 的
     * 镜像标记：任何入口差异导致镜像不同步时，Esc 都不能失灵。
     */
    if ((viewer.getSmartDimensionSnapshot?.()?.state ?? 'inactive') !== 'inactive') {
      // Esc 语义由控制器状态机裁决（撤回上一次选择 / 回退放置 / 空选择退出）。
      viewer.escapeSketchSmartDimension?.();
      if (viewer.getSmartDimensionSnapshot?.()?.state === 'inactive') {
        smartDimensionActive.value = false;
        sketchStateStore.setViewerMode(ViewerMode.Sketch);
        if (isSmartDimensionFamilyCommand(activeOperationCommand.value ?? '')) {
          syncToolbarOperationState(null, null);
        }
      }
      return;
    }
    //------ 智能尺寸命令 结束------
    if (options.isPlacingComponent?.value) {
      options.cancelComponentPlacement?.();
      syncToolbarOperationState(null, null);
      message.info('已取消放置零部件');
      requestDesignTreeAfterSketchCommand(
        EditorPanelShowDesignTreeReason.CommandCanceled,
        'assembly-insert-component',
      );
      return;
    }
    if (isExtrudeMode.value) {
      if (cancelExtrude()) {
        message.info('已取消拉伸');
        requestDesignTreeAfterSketchCommand(
          EditorPanelShowDesignTreeReason.CommandCanceled,
          extrudeUiState.value.operation === 'cut' ? 'extrude-cut' : 'extrude',
        );
      }
      return;
    }
    if (revolveUiState.value.active) {
      if (cancelRevolve()) {
        message.info('已取消旋转凸台');
        requestDesignTreeAfterSketchCommand(
          EditorPanelShowDesignTreeReason.CommandCanceled,
          'revolve',
        );
      }
      return;
    }
    if (revolveCutUiState.value.active) {
      if (cancelRevolveCut()) {
        message.info('已取消旋转切除');
        requestDesignTreeAfterSketchCommand(
          EditorPanelShowDesignTreeReason.CommandCanceled,
          'revolve-cut',
        );
      }
      return;
    }
    if (activeOperationCommand.value === 'assembly-insert-component') {
      options.cancelInsertComponent?.();
      syncToolbarOperationState(null, null);
      message.info('已取消插入零部件');
      requestDesignTreeAfterSketchCommand(
        EditorPanelShowDesignTreeReason.CommandCanceled,
        'assembly-insert-component',
      );
      return;
    }
    if (isReferenceFeatureCommand(activeOperationCommand.value)) {
      const activeReferenceCommand = activeOperationCommand.value;
      const commandMeta = resolveReferenceFeatureCommandMeta(activeReferenceCommand);
      cancelReferenceFeatureCommand(activeReferenceCommand);
      message.info(`已取消${commandMeta.name}`);
      requestDesignTreeAfterSketchCommand(
        EditorPanelShowDesignTreeReason.CommandCanceled,
        activeReferenceCommand,
      );
      return;
    }
    if (activeOperationCommand.value === 'geometry-reference-point') {
      cancelReferencePoint();
      message.info('已取消基准点');
      requestDesignTreeAfterSketchCommand(
        EditorPanelShowDesignTreeReason.CommandCanceled,
        'geometry-reference-point',
      );
      return;
    }
    //------ 基准面命令 ESC 取消 结束------
    if (isConvertEntitiesWorkflowActive()) {
      clearConvertEntitiesWorkflow();
      message.info('已退出转换实体引用');
      requestDesignTreeAfterSketchCommand(
        EditorPanelShowDesignTreeReason.CommandCanceled,
        'sketch-convert-entities',
      );
      return;
    }
    if (isSketchEntitiesTransformWorkflowActive()) {
      const command = sketchEntitiesTransformState.value.command;
      clearSketchEntitiesTransformWorkflow();
      message.info('已退出草图实体变换');
      requestDesignTreeAfterSketchCommand(
        EditorPanelShowDesignTreeReason.CommandCanceled,
        command,
      );
      return;
    }
    if (isSketchCircularPatternWorkflowActive()) {
      cancelSketchCircularPatternWorkflow();
      message.info('已退出圆周草图阵列');
      return;
    }
    if (isSketchOffsetWorkflowActive()) {
      // 退出提示由 cad-core SketchOffsetController.clearMode 的 drawingNoticeCallback 统一输出，
      // 这里不再重复 message.info，避免同一次退出出现两条相同提示（对齐修剪/变换的提示语义）。
      clearSketchOffsetWorkflow();
      requestDesignTreeAfterSketchCommand(
        EditorPanelShowDesignTreeReason.CommandCanceled,
        'sketch-offset-entities',
      );
      return;
    }
    if (isBlendMode.value) {
      const command = activeBlendKind.value;
      cancelBlend();
      message.info('已取消圆角/倒角');
      requestDesignTreeAfterSketchCommand(
        EditorPanelShowDesignTreeReason.CommandCanceled,
        command,
      );
      return;
    }
    if (!isDrawingMode.value && sketchCornerEnable.value) {
      const command = activeOperationCommand.value;
      sketchCornerEnable.value = false;
      viewer.clearSketchCornerMode?.(true);
      requestDesignTreeAfterSketchCommand(
        EditorPanelShowDesignTreeReason.CommandCanceled,
        command,
      );
      return;
    }
    if (isSketchTrimWorkflowActive()) {
      const command = activeOperationCommand.value;
      clearSketchTrimWorkflow();
      message.info('已退出修剪实体');
      requestDesignTreeAfterSketchCommand(
        EditorPanelShowDesignTreeReason.CommandCanceled,
        command,
      );
      return;
    }
    if (hasPendingSketchCommand.value && isIdleViewerModeActive.value) {
      exitSketchArmedState();
      return;
    }
    if (isSketchEditMode.value) {
      if (currentDrawingTool.value) {
        void handleDrawingCancel();
      }
      return;
    }
    const viewMode = viewer.getCurrentViewMode?.() ?? ViewMode.VIEW_3D;
    if (viewMode === ViewMode.DATUM_PLANE) {
      viewer.exitDatumPlaneView();
      currentDrawingDatumPlaneId.value = -1;
      planeStore.clearSelectedDatumPlanePreview(viewer);
      message.info('已退出基准面视图');
    }
  };

  /**
   * 处理视口选择变化。
   *
   * @param meshId 选中对象 ID。
   */
  //------ 原点双向选择同步 开始------
  /**
   * 将视口中的正式基准点选中同步到特征树。
   *
   * @param meshId 当前视口主选对象 ID。
   * @returns 若已命中并完成基准点同步则返回 `true`。
   */
  /** 防止 clearSelectionProgrammatically 触发 SelectionCleared 事件后重入死循环的标志位 */
  let isDatumPointSyncInProgress = false;

  //------ 视口空白点击清理特征树选中 开始------
  /** 防止“空白点击清理特征树选中”在 SelectionUpdated 回流链中重入。 */
  let isViewportClearFeatureTreeSyncInProgress = false;

  /**
   * 将视口空白点击导致的清空选择同步回特征树。
   *
   * 设计说明：
   * 1. 特征树选中特征节点后，会主动同步场景主选；
   * 2. 反向链路也必须闭合：当用户在视口点击空白清空选择时，树上的对应选中态也要一起移除；
   * 3. 这里统一回调 `handleFeatureSelect([])`，让草图引用节点临时显示恢复、缩放控制关闭、
   *    基准面预览清理等副作用继续走同一条收口链；
   * 4. 重入通过 `isViewportClearFeatureTreeSyncInProgress` 统一拦截，避免再次触发循环清空。
   *
   * @param meshId 当前视口主选对象 ID。
   * @param selectionIntent 当前选择更新意图。
   * @returns 若已同步清理特征树选中则返回 `true`。
   */
  const syncViewportClearedSelectionToFeatureTree = async (
    meshId: string | null,
    selectionIntent?: ViewportSelectionSnapshot['selectionIntent'],
  ): Promise<boolean> => {
    if (meshId) {
      return false;
    }
    if (selectionIntent === 'feature-tree-visual-scope') {
      return false;
    }
    if (isViewportClearFeatureTreeSyncInProgress) {
      return true;
    }

    const selectedKey = selectedKeys.value[0] ?? null;
    if (!selectedKey) {
      return false;
    }

    const selectedNodeMeta = featureTreeStore.featureTreeNodeMap.get(selectedKey);
    if (
      !selectedNodeMeta
      || (
        selectedNodeMeta.kind !== FeatureTreeNodeKind.Object
        && selectedNodeMeta.kind !== FeatureTreeNodeKind.DatumPlane
        && selectedNodeMeta.kind !== FeatureTreeNodeKind.DatumAxis
        && selectedNodeMeta.kind !== FeatureTreeNodeKind.DatumPoint
      )
    ) {
      return false;
    }

    isViewportClearFeatureTreeSyncInProgress = true;
    try {
      await featureTreeStore.handleFeatureSelect([]);
      return true;
    } finally {
      isViewportClearFeatureTreeSyncInProgress = false;
    }
  };
  //------ 视口空白点击清理特征树选中 结束------

  const syncDatumPointSelectionToFeatureTree = async (meshId: string | null): Promise<boolean> => {
    //------ 原点双向选择同步 开始------
    // 重入保护：整个同步过程中（无论选中还是取消选中方向），handleFeatureSelect 内部
    // 会调用 selectEntityByObjectId 或 clearSelectionProgrammatically，
    // 这两个操作均会同步派发 EntitySelected / SelectionCleared 事件，
    // 若不在函数入口统一拦截，会导致循环调用 → Maximum call stack exceeded
    if (isDatumPointSyncInProgress) {
      return false;
    }

    isDatumPointSyncInProgress = true;
    try {
      if (!meshId) {
        const selectedKey = selectedKeys.value[0] ?? null;
        const isDatumPointNodeSelected = typeof selectedKey === 'string'
          && (selectedKey === 'origin-node' || selectedKey.startsWith('datum-point-'));
        if (!isDatumPointNodeSelected) {
          return false;
        }
        await featureTreeStore.handleFeatureSelect([]);
        return true;
      }

      const viewer = getViewer();
      if (!viewer) {
        return false;
      }

      const datumPoints = viewer.datumPointManager?.getAllPoints?.() ?? [];
      const matchedDatumPoint = datumPoints.find((datumPoint) => datumPoint.uuid === meshId);
      if (!matchedDatumPoint) {
        return false;
      }

      const targetTreeKey = matchedDatumPoint.isOrigin
        ? 'origin-node'
        : `datum-point-${matchedDatumPoint.id}`;

      const currentSelectedKey = selectedKeys.value[0] ?? null;
      if (selectedKeys.value.length === 1 && currentSelectedKey === targetTreeKey) {
        return true;
      }

      await featureTreeStore.handleFeatureSelect([targetTreeKey]);
      return true;
    } finally {
      isDatumPointSyncInProgress = false;
    }
    //------ 原点双向选择同步 结束------
  };
  //------ 原点双向选择同步 结束------

  //------ 草图空选显示设计树 开始------
  /**
   * 判断草图空选后是否应该回到设计树。
   *
   * @returns 应回到设计树时返回 true。
   */
  function shouldShowDesignTreeAfterSketchEmptySelection(): boolean {
    /**
     * 点击尺寸线/界线时，cad-core 会清空普通草图实体选择，但唯一尺寸仍由独立仓库保持选中。
     * 这不是“属性目标为空”，不能把刚打开的尺寸 PropertyManager 切回设计树。
     */
    const selectedSketchDimensionCount = getViewer()?.getSelectedSketchDimensions?.().length ?? 0;

    return rightPanelTab.value === 'property'
      && viewerMode.value === ViewerMode.Sketch
      && activeOperationCommand.value === null
      && currentDrawingCommand.value === null
      && sketchTrimMode.value === null
      && !hasPendingSketchCommand.value
      && selectedSketchDimensionCount !== 1;
  }

  /**
   * 处理视口空选择。
   *
   * 设计说明：
   * 1. 只有普通草图编辑态的单实体属性页需要在空选后回到设计树；
   * 2. 绘制、修剪、圆角倒角、实体变换等命令态仍由各自 PropertyManager 承载，不被空选打断。
   *
   * @param _source 空选择来源。
   */
  const handleViewportEmptySelectionChange = (_source: EditorViewportEmptySelectionSource): void => {
    if (!shouldShowDesignTreeAfterSketchEmptySelection()) {
      return;
    }

    requestDesignTreeAfterSketchCommand(
      EditorPanelShowDesignTreeReason.SelectionCleared,
      null,
    );
  };
  //------ 草图空选显示设计树 结束------

  /**
   * armed 态选中草图宿主后执行 pending 命令收尾。
   */
  const finalizeArmedSketchHostSelection = async (): Promise<void> => {
    const pendingCommand = pendingSketchToolCommand.value;
    sketchStateStore.clearPendingSketchToolCommand();
    if (pendingCommand && pendingCommand !== 'sketch-enter') {
      await handleSketchCommand(pendingCommand);
    }
  };

  const handleViewportSelectionChange = async (
    selection: ViewportSelectionSnapshot | string | null,
  ): Promise<void> => {
    let meshId: string | null = null;
    let selectionSnapshot: ViewportSelectionSnapshot | undefined;
    if (typeof selection === 'string') {
      meshId = selection;
    } else if (selection) {
      meshId = selection.objectId;
      selectionSnapshot = selection;
    }

    if (isExtrudeMode.value) {
      const handled = await handleExtrudeSelectionChange(selectionSnapshot ?? meshId);
      if (handled) {
        return;
      }
    }
    if (revolveUiState.value.active) {
      const handled = await handleRevolveSelectionChange(selectionSnapshot ?? meshId);
      if (handled) {
        return;
      }
    }
    if (revolveCutUiState.value.active) {
      const handled = await handleRevolveCutSelectionChange(selectionSnapshot ?? meshId);
      if (handled) {
        return;
      }
    }
    if (isBlendMode.value) {
      const handled = await handleBlendSelectionChange(selectionSnapshot);
      if (handled) {
        return;
      }
    }

    //------ 原点双向选择同步 开始------
    const datumPointSelectionSynced = await syncDatumPointSelectionToFeatureTree(meshId);
    if (datumPointSelectionSynced) {
      return;
    }
    //------ 原点双向选择同步 结束------

    //------ 视口空白点击清理特征树选中 开始------
    const featureTreeSelectionCleared = await syncViewportClearedSelectionToFeatureTree(
      meshId,
      selectionSnapshot?.selectionIntent,
    );
    if (featureTreeSelectionCleared) {
      return;
    }
    //------ 视口空白点击清理特征树选中 结束------

    if (hasPendingSketchCommand.value && isIdleViewerModeActive.value && meshId) {
      const viewer = getViewer();
      const selectedDatumPlaneIdFromViewport = viewer?.getSelectedDatumPlaneId?.() ?? null;
      if (selectedDatumPlaneIdFromViewport !== null) {
        const plane = viewer?.getDatumPlaneSummary(selectedDatumPlaneIdFromViewport) ?? null;
        await enterSketchEditingOnDatumPlane(
          selectedDatumPlaneIdFromViewport,
          plane?.name,
        );
        await finalizeArmedSketchHostSelection();
        return;
      }

      const selectedPlanarFace = viewer?.getSelectedPlanarFace?.() ?? null;
      if (selectedPlanarFace) {
        if (!selectedPlanarFace.isPlanar) {
          message.warning('当前仅支持在平面面上进入2D草图');
          return;
        }
        await enterSketchEditingOnPlanarFace(selectedPlanarFace);
        await finalizeArmedSketchHostSelection();
        return;
      }
    }
    if (!isSketchTrimWorkflowActive()) {
      selectedSketchTrimEntity.value = null;
      return;
    }
    if (!meshId) {
      selectedSketchTrimEntity.value = null;
      return;
    }
    selectedSketchTrimEntity.value = {
      objectId: meshId,
      segmentIndex: null,
    };
  };

  /**
   * 处理编辑器顶层命令。
   *
   * @param commandType 命令标识。
   */
  const handleCommand = async (commandType: string): Promise<void> => {
    const isDrawingCommand = isSketchDrawingCommandByManager(commandType);
    const isSketchCommand = commandType.startsWith('sketch-');
    const isTrimCommand = isSketchTrimCommand(commandType);
    const shouldPreserveProfileFeatureSelection = shouldPreserveSelectionForProfileFeatureCommand(commandType);
    const shouldPreserveCommandSelection = shouldPreserveSelectionForCommandStart(commandType);

    if (!canExecuteImportedDocumentToolbarCommand({
      canShowFeatureToolbarTab: options.supportsFeatureCommands.value === true,
      canExecuteFeatureCommands: options.supportsFeatureCommands.value === true,
    }, commandType)) {
      message.warning('装配体标签页不支持特征操作');
      return;
    }

    if ((isSketchActive.value || hasPendingSketchCommand.value) && !isSketchCommand) {
      /**
       * SolidWorks 中从草图态直接点击特征命令会自动退出草图并进入特征 PropertyManager。
       * 这里复用统一退出草图链路，只给“命令切换”开放停止绘制工具的权限，避免按钮层散落特判。
       */
      const exited = await exitSketchMode({
        silent: true,
        allowDrawingStop: true,
      });
      if (!exited) {
        return;
      }
    }
    //------ 上色草图轮廓显示开关 开始------
    /**
     * SolidWorks 中“上色草图轮廓”是草图工具栏上的显示开关（checkable toggle），
     * 不是绘制/编辑命令：绘制进行中切换该开关不应终止当前绘制会话，
     * 也不应触发下方的“命令切换收口”（修剪/转换/实体变换清理与停绘）。
     * 因此必须在收口之前单独路由到 handleSketchCommand 的开关分支并返回，
     * 让当前绘制会话与工具栏按钮高亮保持一致（绘制继续、按钮保持选中）。
     */
    if (commandType === 'sketch-shaded-sketch-contours') {
      await handleSketchCommand(commandType);
      return;
    }
    //------ 上色草图轮廓显示开关 结束------
    const profileFeaturePreselectionSnapshot = shouldPreserveProfileFeatureSelection
      ? readProfileFeaturePreselectionSnapshot()
      : [];

    try {
      //------ 等距实体命令切换收口 开始------
      /**
       * 等距实体是草图内独占命令会话：切换到其他命令时必须先退出，
       * 否则 SketchOffsetController 的事件订阅、选中链与预览对象会残留，
       * 后续绘制/选择操作仍会触发等距实体预览生成（对齐修剪/圆角/变换的收口模式）。
       * 注意：相同命令重入（再次点击等距实体按钮）的退出语义由
       * handleSketchCommand 的 toggle 分支处理，这里只负责“切换其他命令”时的清理。
       */
      if (commandType !== 'sketch-offset-entities' && isSketchOffsetWorkflowActive()) {
        clearSketchOffsetWorkflow();
      }
      //------ 等距实体命令切换收口 结束------
      if (!isTrimCommand) {
        clearSketchTrimWorkflow({
          clearSelection: !shouldPreserveCommandSelection,
        });
      }
      if (commandType !== 'sketch-convert-entities') {
        clearConvertEntitiesWorkflow();
      }
      if (!isSketchEntitiesTransformCommand(commandType)) {
        clearSketchEntitiesTransformWorkflow();
      }
      if (commandType !== 'sketch-circular-pattern') {
        clearSketchCircularPatternWorkflow();
      }
      if (
        isReferenceFeatureCommand(activeOperationCommand.value)
        && activeOperationCommand.value !== commandType
      ) {
        cancelReferenceFeatureCommand(activeOperationCommand.value);
      }
      if (isExtrudeMode.value && commandType !== 'extrude' && commandType !== 'extrude-cut') {
        cancelExtrude();
      }
      if (revolveUiState.value.active && commandType !== 'revolve-boss') {
        cancelRevolve();
      }
      if (revolveCutUiState.value.active && commandType !== 'revolve-cut') {
        cancelRevolveCut();
      }
      if (isBlendMode.value && commandType !== 'fillet' && commandType !== 'chamfer') {
        cancelBlend();
      }
      if (isDrawingMode.value && !isDrawingCommand) {
        /**
         * 命令切换导致停绘时，必须完整关闭绘制会话状态：
         * 1. currentDrawingCommand 是工具栏高亮回退源（syncToolbarOperationState 在
         *    无新命令时会把它写回 activeCommands），不清空会在后续命令退出后
         *    出现“绘制已停止但按钮仍高亮”的状态漂移；
         * 2. 绘制提示计时器与绘制会话同生命周期，停绘时应一并停止；
         * 3. 必须先清 currentDrawingCommand 再同步工具栏，避免回退源把已停止的
         *    绘制命令重新写回高亮。
         */
        getViewer()?.stopSketchDrawing();
        sketchStateStore.setViewerMode(ViewerMode.Sketch);
        currentDrawingTool.value = null;
        currentDrawingCommand.value = null;
        drawingStore.stopDrawingHintTimer();
        syncToolbarOperationState(null, null);
      }
      if (isSketchCornerMode(commandType)) {
        await handleSketchCommand(commandType);
        return;
      }
      if (sketchCornerEnable.value) {
        sketchCornerEnable.value = false;
        getViewer()?.clearSketchCornerMode?.(false);
        if (activeOperationCommand.value !== null && isSketchCornerMode(activeOperationCommand.value)) {
          syncToolbarOperationState(null, null);
        }
      }
      if (commandType.startsWith('sketch-')) {
        await handleSketchCommand(commandType);
        return;
      }
      if (commandType === 'geometry-reference-plane') {
        const viewer = requireViewer();
        viewer.referencePlaneController.open();
        sketchStateStore.setViewerMode(ViewerMode.ReferencePlane);
        rightPanelTab.value = 'property';
        const commandMeta = resolveReferenceFeatureCommandMeta(commandType);
        syncToolbarOperationState(commandType, {
          name: commandMeta.name,
          hint: commandMeta.hint,
        });
        return;
      }
      if (commandType === 'geometry-reference-axis') {
        const viewer = requireViewer() as unknown as {
          referenceAxisController?: {
            open: () => void;
          };
        };
        viewer.referenceAxisController?.open();
        sketchStateStore.setViewerMode(ViewerMode.ReferenceAxis);
        rightPanelTab.value = 'property';
        const commandMeta = resolveReferenceFeatureCommandMeta(commandType);
        syncToolbarOperationState(commandType, {
          name: commandMeta.name,
          hint: commandMeta.hint,
        });
        return;
      }
      if (commandType === 'geometry-reference-point') {
        const viewer = requireViewer() as unknown as {
          referencePointController?: {
            open: (preselected?: PointSelectionCandidate | null) => void;
            syncSelectionVisuals: () => void;
          };
        };
        viewer.referencePointController?.open(null);
        viewer.referencePointController?.syncSelectionVisuals();
        sketchStateStore.setViewerMode(ViewerMode.ReferencePoint);
        rightPanelTab.value = 'property';
        const commandMeta = resolveReferenceFeatureCommandMeta(commandType);
        syncToolbarOperationState(commandType, {
          name: commandMeta.name,
          hint: commandMeta.hint,
        });
        return;
      }
      if (commandType === 'assembly-insert-component') {
        options.startInsertComponent?.();
        rightPanelTab.value = 'property';
        syncToolbarOperationState('assembly-insert-component', {
          name: '插入零部件',
          hint: '请选择要插入的零件或装配体。',
        });
        return;
      }
      if (commandType === 'extrude') {
        await handleExtrudeCommand(profileFeaturePreselectionSnapshot);
        return;
      }
      if (commandType === 'extrude-cut') {
        await handleCutExtrudeCommand(profileFeaturePreselectionSnapshot);
        return;
      }
      if (commandType === 'revolve-boss') {
        await handleRevolveCommand();
        return;
      }
      if (commandType === 'revolve-cut') {
        await handleRevolveCutCommand();
        return;
      }
      if (commandType === 'fillet') {
        handleFilletCommand();
        return;
      }
      if (commandType === 'chamfer') {
        handleChamferCommand();
      }
    } catch (error) {
      /**
       * stale 只表示旧 Worker 响应被当前历史 / 草图 revision 淘汰，属于命令取消流。
       * 这里继续上报会制造 “Uncaught StaleWorkerResponseError” 假错误。
       */
      if (isStaleWorkerResponseError(error)) {
        return;
      }
      const normalizedError = error instanceof Error
        ? error
        : new Error(`命令执行失败: ${commandType}`);
      message.error(normalizedError.message);
      if (typeof window !== 'undefined' && typeof window.reportError === 'function') {
        window.reportError(normalizedError);
      }
    }
  };

  /**
   * 提交基准面。
   */
  const submitReferencePlane = async (): Promise<void> => {
    const viewer = getViewer();
    if (!viewer) {
      return;
    }
    const result = viewer.referencePlaneController?.submit();
    if (!result?.ok) {
      message.warning(result?.reason ?? '当前基准面条件不足，无法提交');
      return;
    }
    sketchStateStore.setViewerMode(idleViewerMode.value);
    syncToolbarOperationState(null, null);
    await updateState();
  };

  //------ 基准面编辑特征 开始------
  /**
   * 从设计树进入基准面编辑特征会话。
   *
   * 设计说明：
   * 1. 设计树只传递基准面 ID，参数恢复必须由 cad-core 按 calculationSource 完成；
   * 2. 成功恢复后复用基准面 PropertyManager 与工具栏命令态，保证创建和编辑共享同一交互入口；
   * 3. 装配体上下文创建的基准面仍回到装配体参考几何体命令，便于提交时进入装配体历史类型。
   *
   * @param planeId 待编辑的基准面 ID。
   */
  const startEditReferencePlane = async (planeId: number): Promise<void> => {
    if (isSketchActive.value || hasPendingSketchCommand.value) {
      const exited = await exitSketchMode({ silent: true });
      if (!exited) {
        return;
      }
    }
    if (isReferenceFeatureCommand(activeOperationCommand.value)) {
      const activeReferenceCommand = activeOperationCommand.value;
      const activeViewer = getViewer();
      if (isReferencePlaneCommand(activeReferenceCommand)) {
        activeViewer?.referencePlaneController?.cancel();
      } else if (isReferenceAxisCommand(activeReferenceCommand)) {
        activeViewer?.referenceAxisController?.cancel();
      } else {
        activeViewer?.referencePointController?.cancel();
        activeViewer?.referencePointController?.syncSelectionVisuals();
      }
    }
    if (isExtrudeMode.value) {
      cancelExtrude();
    }
    if (revolveUiState.value.active) {
      cancelRevolve();
    }
    if (revolveCutUiState.value.active) {
      cancelRevolveCut();
    }
    if (isBlendMode.value) {
      cancelBlend();
    }
    if (isDrawingMode.value) {
      getViewer()?.stopSketchDrawing();
      sketchStateStore.setViewerMode(ViewerMode.Sketch);
      currentDrawingTool.value = null;
    }

    const viewer = requireViewer();
    const calculationSource = viewer.getDatumPlane(planeId)?.getCalculationSource?.() ?? null;
    const result = viewer.referencePlaneController.openEdit(planeId);
    if (!result.ok) {
      message.warning(result.reason ?? '基准面编辑特征参数恢复失败');
      return;
    }

    const commandType = calculationSource?.creationContext === ReferenceCreationContext.Assembly
      ? 'assembly-reference-plane'
      : 'geometry-reference-plane';
    const commandMeta = resolveReferenceFeatureCommandMeta(commandType);
    sketchStateStore.setViewerMode(
      commandType === 'assembly-reference-plane'
        ? ViewerMode.AssemblyReferencePlane
        : ViewerMode.ReferencePlane,
    );
    rightPanelTab.value = 'property';
    syncToolbarOperationState(commandType, {
      name: commandMeta.name,
      hint: commandMeta.hint,
    });
  };
  //------ 基准面编辑特征 结束------

  //------ 基准轴编辑特征 开始------
  /**
   * 从设计树进入基准轴编辑特征会话。
   *
   * 设计说明：
   * 1. 设计树只传递正式基准轴 ID，参数恢复由 cad-core 控制器完成；
   * 2. 成功恢复后复用基准轴 PropertyManager 与工具栏命令态，保持创建/编辑统一入口；
   * 3. 装配体上下文创建的基准轴进入装配体基准轴命令，便于历史命令类型与行为一致。
   *
   * @param axisId 待编辑的基准轴 ID。
   */
  const startEditReferenceAxis = async (axisId: number): Promise<void> => {
    if (isSketchActive.value || hasPendingSketchCommand.value) {
      const exited = await exitSketchMode({ silent: true });
      if (!exited) {
        return;
      }
    }
    if (isReferenceFeatureCommand(activeOperationCommand.value)) {
      const activeReferenceCommand = activeOperationCommand.value;
      const activeViewer = getViewer();
      if (isReferencePlaneCommand(activeReferenceCommand)) {
        activeViewer?.referencePlaneController?.cancel();
      } else if (isReferenceAxisCommand(activeReferenceCommand)) {
        activeViewer?.referenceAxisController?.cancel();
      } else {
        activeViewer?.referencePointController?.cancel();
        activeViewer?.referencePointController?.syncSelectionVisuals();
      }
    }
    if (isExtrudeMode.value) {
      cancelExtrude();
    }
    if (revolveUiState.value.active) {
      cancelRevolve();
    }
    if (revolveCutUiState.value.active) {
      cancelRevolveCut();
    }
    if (isBlendMode.value) {
      cancelBlend();
    }
    if (isDrawingMode.value) {
      getViewer()?.stopSketchDrawing();
      sketchStateStore.setViewerMode(ViewerMode.Sketch);
      currentDrawingTool.value = null;
    }

    const viewer = requireViewer();
    const targetAxis = viewer.getDatumAxis(axisId);
    const commandType = targetAxis?.creationContext === ReferenceCreationContext.Assembly
      ? 'assembly-reference-axis'
      : 'geometry-reference-axis';
    const result = viewer.referenceAxisController.openEdit(axisId);
    if (!result.ok) {
      message.warning(result.reason ?? '基准轴编辑特征参数恢复失败');
      return;
    }

    const commandMeta = resolveReferenceFeatureCommandMeta(commandType);
    sketchStateStore.setViewerMode(
      commandType === 'assembly-reference-axis'
        ? ViewerMode.AssemblyReferenceAxis
        : ViewerMode.ReferenceAxis,
    );
    rightPanelTab.value = 'property';
    syncToolbarOperationState(commandType, {
      name: commandMeta.name,
      hint: commandMeta.hint,
    });
  };
  //------ 基准轴编辑特征 结束------

  //------ 基准点编辑特征 开始------
  /**
   * 从设计树进入基准点编辑特征会话。
   *
   * 设计说明：
   * 1. 设计树只传递正式基准点 ID，参数恢复由 cad-core 控制器完成；
   * 2. 成功恢复后复用基准点 PropertyManager 与工具栏命令态，保持创建/编辑统一入口；
   * 3. 装配体上下文创建的基准点进入装配体基准点命令，便于历史命令类型与行为一致。
   *
   * @param pointId 待编辑的基准点 ID。
   */
  const startEditReferencePoint = async (pointId: number): Promise<void> => {
    if (isSketchActive.value || hasPendingSketchCommand.value) {
      const exited = await exitSketchMode({ silent: true });
      if (!exited) {
        return;
      }
    }
    if (isReferenceFeatureCommand(activeOperationCommand.value)) {
      const activeReferenceCommand = activeOperationCommand.value;
      const activeViewer = getViewer();
      if (isReferencePlaneCommand(activeReferenceCommand)) {
        activeViewer?.referencePlaneController?.cancel();
      } else if (isReferenceAxisCommand(activeReferenceCommand)) {
        activeViewer?.referenceAxisController?.cancel();
      } else {
        activeViewer?.referencePointController?.cancel();
        activeViewer?.referencePointController?.syncSelectionVisuals();
      }
    }
    if (isExtrudeMode.value) {
      cancelExtrude();
    }
    if (revolveUiState.value.active) {
      cancelRevolve();
    }
    if (revolveCutUiState.value.active) {
      cancelRevolveCut();
    }
    if (isBlendMode.value) {
      cancelBlend();
    }
    if (isDrawingMode.value) {
      getViewer()?.stopSketchDrawing();
      sketchStateStore.setViewerMode(ViewerMode.Sketch);
      currentDrawingTool.value = null;
    }

    const viewer = requireViewer();
    const targetPoint = viewer.getDatumPoint(pointId);
    const commandType = targetPoint?.creationContext === ReferenceCreationContext.Assembly
      ? 'assembly-reference-point'
      : 'geometry-reference-point';
    const result = viewer.referencePointController.openEdit(pointId);
    if (!result.ok) {
      message.warning(result.reason ?? '基准点编辑特征参数恢复失败');
      return;
    }

    const commandMeta = resolveReferenceFeatureCommandMeta(commandType);
    sketchStateStore.setViewerMode(
      commandType === 'assembly-reference-point'
        ? ViewerMode.AssemblyReferencePoint
        : ViewerMode.ReferencePoint,
    );
    rightPanelTab.value = 'property';
    syncToolbarOperationState(commandType, {
      name: commandMeta.name,
      hint: commandMeta.hint,
    });
  };
  //------ 基准点编辑特征 结束------

  /**
   * 取消基准面命令。
   */
  const cancelReferencePlane = (): void => {
    getViewer()?.referencePlaneController?.cancel();
    sketchStateStore.setViewerMode(idleViewerMode.value);
    syncToolbarOperationState(null, null);
  };

  /**
   * 提交基准轴。
   */
  const submitReferenceAxis = async (): Promise<void> => {
    const viewer = getViewer()
    if (!viewer?.referenceAxisController) {
      return;
    }
    const result = viewer.referenceAxisController.submit();
    if (!result.ok) {
      message.warning(result.reason ?? '当前基准轴条件不足，无法提交');
      return;
    }
    sketchStateStore.setViewerMode(idleViewerMode.value);
    syncToolbarOperationState(null, null);
    await updateState();
  };

  /**
   * 取消基准轴命令。
   */
  const cancelReferenceAxis = (): void => {
    const viewer = getViewer() as unknown as {
      referenceAxisController?: {
        cancel: () => void;
      };
    } | null;
    viewer?.referenceAxisController?.cancel();
    sketchStateStore.setViewerMode(idleViewerMode.value);
    syncToolbarOperationState(null, null);
  };

  /**
   * 提交基准点。
   */
  const submitReferencePoint = async (): Promise<void> => {
    const viewer = getViewer()
    if (!viewer?.referencePointController) {
      return;
    }
    const result = viewer.referencePointController.submit();
    if (!result.ok) {
      message.warning(result.reason ?? '当前基准点条件不足，无法提交');
      return;
    }
    viewer.referencePointController.syncSelectionVisuals();
    sketchStateStore.setViewerMode(idleViewerMode.value);
    syncToolbarOperationState(null, null);
    await updateState();
  };

  /**
   * 取消基准点命令。
   */
  const cancelReferencePoint = (): void => {
    const viewer = getViewer()
    viewer?.referencePointController?.cancel();
    viewer?.referencePointController?.syncSelectionVisuals();
    sketchStateStore.setViewerMode(idleViewerMode.value);
    syncToolbarOperationState(null, null);
  };

  /**
   * 提交任意参考几何体命令。
   *
   * @param commandType 参考几何体命令。
   */
  const submitReferenceFeatureCommand = async (commandType: ReferenceFeatureCommandType): Promise<void> => {
    if (isReferencePlaneCommand(commandType)) {
      await submitReferencePlane();
      return;
    }
    if (isReferenceAxisCommand(commandType)) {
      await submitReferenceAxis();
      return;
    }
    await submitReferencePoint();
  };

  /**
   * 取消任意参考几何体命令。
   *
   * @param commandType 参考几何体命令。
   */
  const cancelReferenceFeatureCommand = (commandType: ReferenceFeatureCommandType): void => {
    if (isReferencePlaneCommand(commandType)) {
      cancelReferencePlane();
      return;
    }
    if (isReferenceAxisCommand(commandType)) {
      cancelReferenceAxis();
      return;
    }
    cancelReferencePoint();
  };

  /**
   * 提交装配体基准面命令。
   */
  const submitAssemblyReferencePlane = async (): Promise<void> => {
    await submitReferencePlane();
  };

  /**
   * 取消装配体基准面命令。
   */
  const cancelAssemblyReferencePlane = (): void => {
    cancelReferencePlane();
  };

  /**
   * 提交装配体基准轴命令。
   */
  const submitAssemblyReferenceAxis = async (): Promise<void> => {
    await submitReferenceAxis();
  };

  /**
   * 取消装配体基准轴命令。
   */
  const cancelAssemblyReferenceAxis = (): void => {
    cancelReferenceAxis();
  };

  /**
   * 提交装配体基准点命令。
   */
  const submitAssemblyReferencePoint = async (): Promise<void> => {
    await submitReferencePoint();
  };

  /**
   * 取消装配体基准点命令。
   */
  const cancelAssemblyReferencePoint = (): void => {
    cancelReferencePoint();
  };

  /**
   * 撤销当前绘图点。
   */
  const handleDrawingUndoPoint = (): boolean => {
    return getViewer()?.undoSketchDrawingPoint?.() ?? false;
  };

  /**
   * 退出草图模式。
   *
   * @param input 退出选项。
   * @returns 是否退出成功。
   */
  const exitSketchMode = async (input: ExitSketchModeInput = {}): Promise<boolean> => {
    const {
      silent = false,
      allowDrawingStop = false,
    } = input;

    if (hasPendingSketchCommand.value && isIdleViewerModeActive.value) {
      exitSketchArmedState();
      return true;
    }
    if (!isSketchEditMode.value) {
      return true;
    }
    if (!canExitSketch({ allowDrawingStop })) {
      return false;
    }

    const viewer = getViewer();
    const activeSketchGroupIdBeforeExit = viewer?.getActiveSketchGroupId?.() ?? currentSketchGroupId.value;
    let sketchSessionExitHistoryDraft: SketchSessionExitHistoryDraft | null = activeSketchGroupIdBeforeExit
      ? captureSketchSessionExitHistory?.({
        sketchGroupId: activeSketchGroupIdBeforeExit,
        label: '退出草图',
        commandType: 'sketch.session.exit',
      }) ?? null
      : null;
    if (viewer) {
      if (isDrawingMode.value) {
        // 特征命令切换只保留已经落地的草图实体，未提交的绘制预览由 cad-core 统一清理。
        viewer.stopSketchDrawing();
      }
      const viewMode = viewer.getCurrentViewMode?.() ?? ViewMode.VIEW_3D;
      if (viewMode === ViewMode.DATUM_PLANE) {
        viewer.exitDatumPlaneView();
      }
    }

    currentDrawingTool.value = null;
    currentDrawingCommand.value = null;
    viewer?.clearSketchCornerMode?.(false);
    clearSketchTrimWorkflow({ clearToolbarState: false, clearSelection: false });
    clearConvertEntitiesWorkflow({ clearToolbarState: false });
    clearSketchEntitiesTransformWorkflow({ clearToolbarState: false });
    clearSketchCircularPatternWorkflow({ clearToolbarState: false });
    syncToolbarOperationState(null, null);

    let sketchSessionEndError: unknown = null;
    let sketchSessionEnded = false;
    let sketchSessionExitHistoryCommitted = false;
    try {
      const sketchSessionEndResult = await (viewer?.endSketchSession?.({
        discardEmpty: true,
      }) ?? Promise.resolve(null));
      sketchSessionEnded = true;
      if (sketchSessionExitHistoryDraft && sketchSessionEndResult?.endedGroupId && commitSketchSessionExitHistory) {
        await commitSketchSessionExitHistory(sketchSessionExitHistoryDraft, {
          removed: sketchSessionEndResult.removed,
        });
        sketchSessionExitHistoryCommitted = true;
      }
      /**
       * 阶段 B 可能在草图会话结束时把转换实体引用更新为 dangling。
       * 设计树使用的是状态投影快照，必须在会话与退出历史都落定后统一刷新，
       * 否则草图节点会继续显示退出前的旧 warning 状态。
       */
      await updateState();
    } catch (error) {
      sketchSessionEndError = error;
    } finally {
      if (sketchSessionExitHistoryDraft && !sketchSessionExitHistoryCommitted) {
        releaseSketchSessionExitHistory?.(sketchSessionExitHistoryDraft);
        sketchSessionExitHistoryDraft = null;
      }
    }

    /**
     * 草图终止没有完成时，cad-core 会恢复同一个草图 Edit 会话。
     * web-client 必须保留草图身份并回到基础 Sketch 模式，不能提交 idle 退出状态。
     */
    const cadCoreRestoredSketchSession = (
      !!sketchSessionEndError
      && !sketchSessionEnded
      && !!activeSketchGroupIdBeforeExit
      && viewer?.getActiveSketchGroupId?.() === activeSketchGroupIdBeforeExit
    );
    if (
      sketchSessionEndError
      && !sketchSessionEnded
      && (
        cadCoreRestoredSketchSession
        || !isStaleWorkerResponseError(sketchSessionEndError)
      )
    ) {
      currentSketchGroupId.value = activeSketchGroupIdBeforeExit;
      sketchStateStore.setViewerMode(ViewerMode.Sketch);
      if (isStaleWorkerResponseError(sketchSessionEndError)) {
        return false;
      }
      throw sketchSessionEndError;
    }

    /**
     * clearSketch*EntitiesMode（含 SDK 实现和测试 mock）可能把 viewerMode 回写为 Sketch，
    * 因此只有 cad-core 确认结束后，才统一提交 idle 与退出清理。
    */
    sketchStateStore.setViewerMode(idleViewerMode.value);
    planeStore.restoreSketchPlaneVisibility(viewer);
    planeStore.restoreDefaultPlaneSnapshot(undefined, viewer);
    planeStore.clearDefaultPlaneSnapshot();
    currentDrawingDatumPlaneId.value = -1;
    currentSketchGroupId.value = null;
    selectedDatumPlaneId.value = -1;
    planeStore.clearSelectedDatumPlanePreview(viewer);
    sketchStateStore.clearPendingSketchToolCommand();

    /**
     * History 提交发生在 cad-core 草图会话成功结束之后。
     * 此处失败不能重新伪造编辑会话，但异常仍必须交给上层报告。
     */
    if (sketchSessionEndError && !isStaleWorkerResponseError(sketchSessionEndError)) {
      throw sketchSessionEndError;
    }

    if (!silent) {
      message.info('已退出草图');
    }
    return true;
  };

  /**
   * 处理草图命令。
   *
   * @param commandType 命令标识。
   */
  const handleSketchCommand = async (commandType: string): Promise<void> => {
    let drawingTool: SketchDrawingTool | null = null;

    //------ 智能尺寸命令 开始------
    if (isSmartDimensionFamilyCommand(commandType)) {
      // 再次点击同一按钮 = 退出命令（与 SOLIDWORKS 工具按钮切换语义一致）；
      // 家族内切换命令 = 先收口当前会话再启动新变体。
      const isSameActiveCommand = smartDimensionActive.value
        && activeOperationCommand.value === commandType;
      if (smartDimensionActive.value) {
        stopSmartDimensionWorkflow();
      }
      if (!isSameActiveCommand) {
        await startSmartDimensionWorkflow(commandType);
      }
      return;
    }
    // 其他草图命令启动前收口智能尺寸命令会话，保持命令互斥。
    if (smartDimensionActive.value) {
      stopSmartDimensionWorkflow();
    }
    //------ 智能尺寸命令 结束------

    /**
     * 同步直线命令到直线绘制选项。
     * @param command 直线命令。
     */
    const syncLineDrawingMode = (command: SketchLineCommand): void => {
      const viewer = getViewer();
      const currentOptions = viewer?.getLineDrawingOptions?.();
      if (!viewer || !currentOptions) {
        return;
      }
      viewer.setLineDrawingOptions?.({
        ...currentOptions,
        type: command,
      });
    };

    /**
     * 同步矩形命令到矩形绘制选项。
     * @param command 矩形命令。
     */
    const syncRectangleDrawingMode = (command: SketchRectangleCommand): void => {
      const viewer = getViewer();
      const currentOptions = viewer?.getRectangleDrawingOptions?.();
      if (!viewer || !currentOptions) {
        return;
      }
      //------ 中心矩形强制构造线配置 开始------
      viewer.setRectangleDrawingOptions?.({
        ...currentOptions,
        mode: command,
      });
      //------ 中心矩形强制构造线配置 结束------
    };

    /**
     * 同步圆命令到圆绘制选项。
     * @param command 圆命令。
     */
    const syncCircleDrawingMode = (command: SketchCircleCommand): void => {
      const viewer = getViewer();
      const currentOptions = viewer?.getCircleDrawingOptions?.();
      if (!viewer || !currentOptions) {
        return;
      }
      viewer.setCircleDrawingOptions?.({
        ...currentOptions,
        mode: command,
      });
    };

    /**
     * 同步圆弧命令到圆弧绘制选项。
     * @param command 圆弧命令。
     */
    const syncArcDrawingMode = (command: SketchArcCommand): void => {
      const viewer = getViewer();
      const currentOptions = viewer?.getArcDrawingOptions?.();
      if (!viewer || !currentOptions) {
        return;
      }
      viewer.setArcDrawingOptions?.({
        ...currentOptions,
        mode: command,
      });
    };

    if (isSketchLineCommand(commandType)) {
      drawingTool = SketchDrawingTool.Line;
      syncLineDrawingMode(commandType);
    }
    if (isSketchRectangleCommand(commandType)) {
      drawingTool = SketchDrawingTool.Rectangle;
      syncRectangleDrawingMode(commandType);
    }
    if (isSketchCircleCommand(commandType)) {
      drawingTool = SketchDrawingTool.Circle;
      syncCircleDrawingMode(commandType);
    }
    if (isSketchArcCommand(commandType)) {
      drawingTool = SketchDrawingTool.Arc;
      syncArcDrawingMode(commandType);
    }
    if (isSketchEllipseCommand(commandType)) {
      drawingTool = SketchDrawingTool.Ellipse;
      getViewer()?.setEllipseDrawingMode?.(commandType);
    }
    if (isSketchSlotCommand(commandType)) {
      drawingTool = SketchDrawingTool.Slot;
      getViewer()?.setSlotDrawingMode?.(commandType as SketchSlotCommand);
    }
    if (isSketchPolygonCommand(commandType)) {
      drawingTool = SketchDrawingTool.Polygon;
    }
    if (isSketchSplineCommand(commandType)) {
      if (commandType === SketchSplineCommand.Spline) {
        drawingTool = SketchDrawingTool.Spline;
      } else if (commandType === SketchSplineCommand.StyleSpline) {
        drawingTool = SketchDrawingTool.StyleSpline;
        getViewer()?.setStyleSplineDrawingMode?.(StyleSplineMode.Bezier);
      } else {
        throw new Error(`未知样条命令: ${commandType}`);
      }
    }
    if (isSketchPointCommand(commandType)) {
      drawingTool = SketchDrawingTool.Point;
    }

    if (commandType === 'sketch-convert-entities') {
      if (!await ensureSketchCommandContext(commandType)) {
        return;
      }

      /**
       * Convert Entities 属于草图内的独占命令会话：
       * 1. 进入前必须终止绘制态，否则选择处理器会继续把点击让给绘制工具；
       * 2. 进入前必须清空旧选择，避免草图实体属性面板残留；
       * 3. `useEditorSketchWorkflow` 只负责切到命令态，并把后续选择 / preview / create 委托给 geometry workflow；
       * 4. 目标草图组、已选来源与 preview/create 链路都由 geometry workflow 统一托管。
       */
      getViewer()?.stopSketchDrawing?.();
      sketchStateStore.setViewerMode(ViewerMode.Sketch);
      selectedSketchTrimEntity.value = null;
      rightPanelTab.value = 'property';
      currentDrawingTool.value = null;
      currentDrawingCommand.value = null;
      drawingStore.stopDrawingHintTimer();
      syncToolbarOperationState(commandType, {
        name: '转换实体引用',
        hint: '请选择要转换到当前草图中的外部实体。',
      });
      enterConvertEntities?.();
      return;
    }
    //------ 草图实体变换 UI 工作流 开始------
    if (isSketchEntitiesTransformCommand(commandType)) {
      await activateSketchEntitiesTransformWorkflow(commandType);
      return;
    }
    //------ 草图实体变换 UI 工作流 结束------
    if (commandType === 'sketch-circular-pattern') {
      if (isSketchCircularPatternWorkflowActive()) {
        cancelSketchCircularPatternWorkflow();
        return;
      }
      await activateSketchCircularPatternWorkflow();
      return;
    }
    if (commandType === 'sketch-shaded-sketch-contours') {
      shadedSketchContoursEnabled.value = !shadedSketchContoursEnabled.value;
      getViewer()?.setShadedSketchContoursEnabled?.(shadedSketchContoursEnabled.value);
      return;
    }
    if (commandType === 'sketch-enter') {
      if (isSketchEditMode.value) {
        if (!hasActiveViewerSketchSession()) {
          clearDetachedSketchRuntimeState();
          pendingSketchToolCommand.value = 'sketch-enter';
          enterSketchArmedState({ message: '请先选择前视/上视/右视基准面或平面面', level: 'warning' });
          return;
        }
        const exited = await exitSketchMode();
        if (!exited) {
          return;
        }
        //------ 退出草图显示设计树 开始------
        /**
         * 手动退出草图后不再保留 PropertyManager 命令会话，
         * 需要复用右侧面板事件让宿主统一切回设计树。
         */
        requestDesignTreeAfterSketchCommand(
          EditorPanelShowDesignTreeReason.CommandFinished,
          'sketch-exit',
        );
        //------ 退出草图显示设计树 结束------
        return;
      }
      if (hasPendingSketchCommand.value && isIdleViewerModeActive.value) {
        pendingSketchToolCommand.value = 'sketch-enter';
        enterSketchArmedState({ message: '请先选择前视/上视/右视基准面或平面面', level: 'warning' });
        return;
      }
      const selectedDatumPlaneNodeMeta = selectedKeys.value
        .map((key) => featureTreeStore.featureTreeNodeMap.get(key))
        .find((nodeMeta) => nodeMeta?.kind === FeatureTreeNodeKind.DatumPlane
          && hasFeatureTreeDatumPlaneId(nodeMeta.planeId));

      if (selectedDatumPlaneNodeMeta?.kind === FeatureTreeNodeKind.DatumPlane
        && hasFeatureTreeDatumPlaneId(selectedDatumPlaneNodeMeta.planeId)) {
        const plane = getViewer()?.getDatumPlaneSummary(selectedDatumPlaneNodeMeta.planeId);
        //------ 用户创建基准面进入草图 开始------
        if (plane) {
          await enterSketchEditingOnDatumPlane(selectedDatumPlaneNodeMeta.planeId, plane.name);
          return;
        }
        //------ 用户创建基准面进入草图 结束------
      }
      const selectedPlanarFace = getViewer()?.getSelectedPlanarFace?.() ?? null;
      if (selectedPlanarFace) {
        if (!selectedPlanarFace.isPlanar) {
          message.warning('当前仅支持在平面面上进入2D草图');
          return;
        }
        await enterSketchEditingOnPlanarFace(selectedPlanarFace);
        sketchStateStore.clearPendingSketchToolCommand();
        return;
      }
      pendingSketchToolCommand.value = 'sketch-enter';
      enterSketchArmedState({ message: '请先选择前视/上视/右视基准面或平面面', level: 'warning' });
      return;
    }
    if (commandType === 'sketch-exit') {
      if (hasPendingSketchCommand.value && isIdleViewerModeActive.value) {
        exitSketchArmedState();
        return;
      }
      const exited = await exitSketchMode();
      if (!exited) {
        return;
      }
      //------ 退出草图显示设计树 开始------
      /**
       * 工具栏“退出草图”走独立 `sketch-exit` 命令，
       * 成功退出后必须通过事件回到设计树，避免右侧停留在属性页。
       */
      requestDesignTreeAfterSketchCommand(
        EditorPanelShowDesignTreeReason.CommandFinished,
        'sketch-exit',
      );
      //------ 退出草图显示设计树 结束------
      return;
    }
    if (isSketchTrimCommand(commandType)) {
      await activateSketchTrimWorkflow(commandType);
      return;
    }
    if (commandType === 'sketch-offset-entities') {
      // 再次点击同一按钮 = 退出命令（与智能尺寸按钮切换语义一致），不生成任何等距实体
      if (isSketchOffsetWorkflowActive()) {
        clearSketchOffsetWorkflow();
        return;
      }
      await activateSketchOffsetWorkflow();
      return;
    }
    if (isSketchCornerMode(commandType)) {
      await activateSketchCornerWorkflow(commandType);
      return;
    }
    if (isDrawingMode.value) {
      getViewer()?.stopSketchDrawing();
    }
    if (!await ensureSketchCommandContext(commandType)) {
      return;
    }
    if (drawingTool) {
      const viewer = getViewer();
      if (!viewer) {
        message.error('视口未初始化');
        return;
      }
      if (!hasActiveViewerSketchSession()) {
        clearDetachedSketchRuntimeState();
        pendingSketchToolCommand.value = commandType;
        enterSketchArmedState({
          message: '请先选择前视/上视/右视基准面或平面面',
          level: 'warning',
        });
        return;
      }
      sketchStateStore.clearPendingSketchToolCommand();
      clearSketchTrimWorkflow({ clearToolbarState: false });
      clearSketchEntitiesTransformWorkflow({ clearToolbarState: false });
      clearSketchCircularPatternWorkflow({ clearToolbarState: false });
      sketchCornerEnable.value = false;
      viewer.clearSketchCornerMode?.(false);
      rightPanelTab.value = 'property';
      sketchStateStore.setViewerMode(ViewerMode.SketchDrawing);
      currentDrawingTool.value = drawingTool;
      currentDrawingCommand.value = commandType;
      syncToolbarOperationState(null, null);

      planeStore.hideActiveDatumPlanePreview(viewer);
      viewer.startDrawing(drawingTool);
      drawingStore.startDrawingHintTimer();
      return;
    }
    message.warning(`草图命令"${commandType}"暂未实现`);
  };

  /**
   * 取消当前绘图。
   */
  const handleDrawingCancel = async (): Promise<void> => {
    const command = currentDrawingCommand.value ?? activeOperationCommand.value;
    if (!isDrawingMode.value && sketchCornerEnable.value) {
      sketchCornerEnable.value = false;
      const viewer = getViewer();
      viewer?.clearSketchCornerMode?.(true);
      viewer?.clearSelectionProgrammatically?.();
      if (activeOperationCommand.value !== null && isSketchCornerMode(activeOperationCommand.value)) {
        syncToolbarOperationState(null, null);
      }
      requestDesignTreeAfterSketchCommand(EditorPanelShowDesignTreeReason.CommandCanceled, command);
      return;
    }
    const viewer = getViewer();
    sketchStateStore.setViewerMode(ViewerMode.Sketch);
    currentDrawingTool.value = null;
    currentDrawingCommand.value = null;
    drawingStore.stopDrawingHintTimer();
    sketchStateStore.clearPendingSketchToolCommand();
    sketchCornerEnable.value = false;
    viewer?.clearSketchCornerMode?.(false);
    clearSketchTrimWorkflow({ clearToolbarState: false });
    clearSketchEntitiesTransformWorkflow({ clearToolbarState: false });
    clearSketchCircularPatternWorkflow({ clearToolbarState: false });
    syncToolbarOperationState(null, null);
    viewer?.stopSketchDrawing();
    // 取消绘图时统一通过选择中心清空当前选中对象，避免属性面板残留旧选择态。
    viewer?.clearSelectionProgrammatically?.();
    await updateState();
    requestDesignTreeAfterSketchCommand(EditorPanelShowDesignTreeReason.CommandCanceled, command);
  };

  /**
   * 完成当前绘图。
   */
  const handleDrawingFinish = async (): Promise<void> => {
    const command = currentDrawingCommand.value ?? activeOperationCommand.value;
    const viewer = getViewer();
    if (!isDrawingMode.value || !viewer) {
      return;
    }
    await viewer.finishCurrentDrawing?.();
    sketchStateStore.setViewerMode(ViewerMode.Sketch);
    currentDrawingTool.value = null;
    drawingStore.stopDrawingHintTimer();
    message.success('绘图已完成');
    requestDesignTreeAfterSketchCommand(EditorPanelShowDesignTreeReason.CommandFinished, command);
  };

  /**
   * 处理键盘事件。
   *
   * @param event 键盘事件。
   */
  const handleKeyDown = (event: KeyboardEvent): void => {
    // 草图绘制进行中，ESC 必须总能退出当前绘制命令：
    // 1. 保留已经生成的正式线段（每段已随 sketch.runtime.draw.* 事务即时提交）；
    // 2. 清理跟随鼠标的预览与未落点（cad-core stopDrawing 内部统一 dispose 预览）；
    // 3. 退出草图-线段等绘制功能。
    // 说明：画完一段后线参数输入框会被自动聚焦（LinePropertyManagerPanel.requestLengthEditorFocus），
    // 若该分支晚于下面的输入态门禁，ESC 会被输入框焦点吞掉导致无法退出绘制，故必须先于门禁处理。
    if (event.key === 'Escape' && isDrawingMode.value) {
      event.preventDefault();
      handleEscKey();
      return;
    }
    if (isEditableKeyboardTarget(event.target)) {
      return;
    }
    const shortcutKey = event.key.toLowerCase();
    const isSketchClipboardShortcut = (
      (event.ctrlKey || event.metaKey)
      && !event.altKey
      && !event.shiftKey
      && (shortcutKey === 'c' || shortcutKey === 'v')
    );
    if (sketchStateStore.viewerMode === ViewerMode.Sketch && isSketchClipboardShortcut) {
      // UI 仅路由快捷键；实体/尺寸混合选择、自由落点、关系复制和历史事务全部由 cad-core 收口。
      const viewer = getViewer();
      const handled = shortcutKey === 'c'
        ? viewer?.copySelectedSketchEntitiesToClipboard?.() ?? false
        : viewer?.pasteSketchEntityClipboardAtLastPointerPosition?.() ?? false;
      if (handled) {
        event.preventDefault();
        message.success(shortcutKey === 'c' ? '已复制草图内容' : '已粘贴草图内容');
      }
      return;
    }
    if (event.key === 'Escape') {
      handleEscKey();
      return;
    }
    if (event.key === 'Enter' && isDrawingMode.value) {
      void handleDrawingFinish();
      return;
    }
    if (event.key === 'Backspace' && isDrawingMode.value) {
      handleDrawingUndoPoint();
      return;
    }
    if (
      event.key === 'Delete'
      && !isDrawingMode.value
      && !convertEntitiesState.value.active
      && !sketchEntitiesTransformState.value.active
    ) {
      // 实体与全部显式选中尺寸必须由 cad-core 在同一事务内删除，UI 不再按主尺寸提前分叉。
      const deleted = getViewer()?.deleteCurrentSketchSelection?.() ?? false;
      if (deleted) {
        event.preventDefault();
        message.success('已删除草图内容');
        void updateState().catch(() => {
          message.error('删除草图内容后刷新状态失败');
        });
      }
    }
  };

  return {
    shadedSketchContoursEnabled,
    activeOperationCommand,
    currentDrawingCommand,
    toolbarOperationOverride,
    sketchCornerEnable,
    sketchCornerMode,
    sketchTrimMode,
    sketchTrimOptions,
    selectedSketchTrimEntity,
    //------ 智能尺寸命令 开始------
    smartDimensionActive,
    stopSmartDimensionWorkflow,
    //------ 智能尺寸命令 结束------
    //------ 等距实体导出 开始------
    sketchOffsetDistance,
    sketchOffsetReverse,
    sketchOffsetBidirectional,
    sketchOffsetAddSize,
    sketchOffsetSelectChain,
    sketchOffsetCover,
    sketchOffsetBasicGeometry,
    sketchOffsetOffsetGeometry,
    //------ 等距实体导出 结束------
    convertEntitiesState,
    sketchEntitiesTransformState,
    linePropertyVisible,
    arcPropertyVisible,
    circlePropertyVisible,
    ellipsePropertyVisible,
    polygonPropertyVisible,
    rectanglePropertyVisible,
    splinePropertyVisible,
    styleSplinePropertyVisible,
    slotPropertyVisible,
    pointPropertyVisible,
    insertLinePropertyVisible,
    selectedObjectName,
    syncToolbarOperationState,
    syncViewportInteractionMode,
    enterSketchEditingOnDatumPlane,
    resumeSketchEditing,
    handleViewportSelectionChange,
    //------ 草图空选显示设计树 开始------
    handleViewportEmptySelectionChange,
    //------ 草图空选显示设计树 结束------
    handleCommand,
    handleSketchCommand,
    handleDrawingCancel,
    handleDrawingFinish,
    handleDrawingUndoPoint,
    handleKeyDown,
    handleEscKey,
    exitSketchMode,
    handlePropertyManagerHelp,
    handlePropertyManagerPreviewToggle,
    submitReferencePlane,
    startEditReferencePlane,
    cancelReferencePlane,
    submitReferenceAxis,
    startEditReferenceAxis,
    cancelReferenceAxis,
    submitReferencePoint,
    startEditReferencePoint,
    cancelReferencePoint,
    submitAssemblyReferencePlane,
    cancelAssemblyReferencePlane,
    submitAssemblyReferenceAxis,
    cancelAssemblyReferenceAxis,
    submitAssemblyReferencePoint,
    cancelAssemblyReferencePoint,
    isReferenceFeatureCommand,
    submitReferenceFeatureCommand,
    cancelReferenceFeatureCommand,
    setSketchCornerModeFromProperty,
    setSketchTrimModeFromProperty,
    setSketchTrimOptionsFromProperty,
    //------ 等距实体属性导出 开始------
    setSketchOffsetDistanceFromProperty,
    setSketchOffsetReverseFromProperty,
    setSketchOffsetBidirectionalFromProperty,
    setSketchOffsetAddSizeFromProperty,
    setSketchOffsetSelectChainFromProperty,
    setSketchOffsetCoverFromProperty,
    setSketchOffsetBasicGeometryFromProperty,
    setSketchOffsetOffsetGeometryFromProperty,
    generateAndExitSketchOffsetWorkflow,
    //------ 等距实体属性导出 结束------
    clearSketchOffsetWorkflow,
    confirmConvertEntitiesWorkflow,
    setSketchEntitiesTransformInputMode: (mode: SketchEntitiesTransformInputMode): void => {
      sketchEntitiesTransformState.value = {
        ...sketchEntitiesTransformState.value,
        inputMode: mode,
        activeSelectionRole: mode === SketchEntitiesTransformInputMode.FromTo
          ? SketchEntitiesTransformSelectionRole.FromPoint
          : SketchEntitiesTransformSelectionRole.Entities,
      };
    },
    setSketchEntitiesTransformSelectionRole: (role: SketchEntitiesTransformSelectionRole): void => {
      sketchEntitiesTransformState.value = {
        ...sketchEntitiesTransformState.value,
        activeSelectionRole: role,
      };
    },
    setSketchEntitiesTransformTargetCoordinate: (axis: 'x' | 'y', value: number | null): void => {
      sketchEntitiesTransformState.value = {
        ...sketchEntitiesTransformState.value,
        targetX: axis === 'x' ? value : sketchEntitiesTransformState.value.targetX,
        targetY: axis === 'y' ? value : sketchEntitiesTransformState.value.targetY,
      };
    },
    setSketchEntitiesTransformAngle: (value: number | null): void => {
      sketchEntitiesTransformState.value = {
        ...sketchEntitiesTransformState.value,
        angleDeg: value,
      };
    },
    syncSketchEntitiesTransformState: (state: SketchEntitiesTransformState): void => {
      //------ 移动实体确认按钮状态同步 开始------
      /**
       * Move PropertyManager 直接消费 cad-core controller 快照，顶部确认按钮却读取宿主 context。
       * 这里把控制器快照回写到宿主状态，避免右上角确认按钮停留在旧的 canConfirm=false。
       */
      sketchEntitiesTransformState.value = {
        ...state,
        selectedItems: [...state.selectedItems],
      };
      //------ 移动实体确认按钮状态同步 结束------
    },
    removeSketchEntitiesTransformSelection: (selectionId: string): void => {
      sketchEntitiesTransformState.value = {
        ...sketchEntitiesTransformState.value,
        selectedItems: sketchEntitiesTransformState.value.selectedItems.filter((item) => item.id !== selectionId),
      };
    },
    repeatSketchEntitiesTransform: (): void => {
      //------ 移动实体面板退出同步 开始------
      const viewer = getViewer();
      const command = sketchEntitiesTransformState.value.command;
      if (command === SketchEntitiesTransformCommand.Move) {
        viewer?.repeatSketchMoveEntities?.();
      } else if (command === SketchEntitiesTransformCommand.Copy) {
        viewer?.repeatSketchCopyEntities?.();
      } else if (command === SketchEntitiesTransformCommand.Rotate) {
        viewer?.repeatSketchRotateEntities?.();
      }
      //------ 移动实体面板退出同步 结束------
    },
    confirmSketchEntitiesTransform: (): void => {
      //------ 移动实体面板退出同步 开始------
      const viewer = getViewer();
      const command = sketchEntitiesTransformState.value.command;
      let confirmed = false;
      if (command === SketchEntitiesTransformCommand.Move) {
        confirmed = viewer?.confirmSketchMoveEntities?.() ?? false;
      } else if (command === SketchEntitiesTransformCommand.Copy) {
        confirmed = viewer?.confirmSketchCopyEntities?.() ?? false;
      } else if (command === SketchEntitiesTransformCommand.Rotate) {
        confirmed = viewer?.confirmSketchRotateEntities?.() ?? false;
      } else if (command === SketchEntitiesTransformCommand.Mirror) {
        confirmed = viewer?.confirmSketchMirrorEntities?.() ?? false;
      }
      if (confirmed) {
        clearSketchEntitiesTransformWorkflow();
        requestDesignTreeAfterSketchCommand(EditorPanelShowDesignTreeReason.CommandConfirmed, command);
      }
      //------ 移动实体面板退出同步 结束------
    },
    cancelSketchEntitiesTransform: (): void => {
      const command = sketchEntitiesTransformState.value.command;
      clearSketchEntitiesTransformWorkflow();
      requestDesignTreeAfterSketchCommand(EditorPanelShowDesignTreeReason.CommandCanceled, command);
    },
    confirmSketchCircularPatternWorkflow,
    cancelSketchCircularPatternWorkflow,
    clearSketchCircularPatternWorkflow,
    isSketchTrimWorkflowActive,
    clearSketchTrimWorkflow,
    clearConvertEntitiesWorkflow,
    clearSketchEntitiesTransformWorkflow,
  };
}
